import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import Select from "react-select";
import countries from "i18n-iso-countries";
import esLocale from "i18n-iso-countries/langs/es.json";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "flatpickr/dist/themes/material_green.css";
import Flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";

import PhoneLoader from "../components/loader/PhoneLoader";
import ScrollToTop from "../components/ScrollTop";
import Header from "../components/Header";
import FmFinalizePopup from "../components/FmFinalizePopup";
import CardsPromotionSelector from "../components/promotions/CardsPromotionSelector";
import SaleReviewPopup from "../components/SaleReviewPopup";
import { useAuth } from "../provider/authProvider";

import {
    handlePhoneInput,
    handleEmailInput,
    handleImeiInput,
    handlePriceInput,
    handleCustomInput,
} from "../utils/inputUtils";

import { buildGroupedSedesOptions } from "../utils/HerlperRegisters";

import "../Cards.css";
import "../styles/CardsCashPriceDifference.css";

const TZ = "America/Lima";

const DNI_API_URL = "https://apiperu.dev/api/dni";
const RUC_API_URL = "https://api.apiperu.dev/ruc";
const DNI_API_BEARER = process.env.REACT_APP_APIPERU_TOKEN;
const RUC_API_BEARER = process.env.REACT_APP_APIPERU_TOKEN;

const axiosDni = axios.create({
    baseURL: DNI_API_URL,
    headers: {
        Authorization: `Bearer ${DNI_API_BEARER || ""}`,
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});

const axiosRuc = axios.create({
    baseURL: RUC_API_URL,
    headers: {
        Authorization: `Bearer ${RUC_API_BEARER || ""}`,
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});

countries.registerLocale(esLocale);

const PERU_ISO2 = "pe";
const PERU_COUNTRY_NAME = "Perú";
const DEFAULT_DOCUMENT_LABEL = "Carnet de extranjería";

const COUNTRY_DIAL_CODE_BY_ISO2 = {
    pe: "+51",
};

const SALE_STATES = [
    { value: 1, label: "Venta pendiente", kind: "warning" },
    { value: 2, label: "Evaluación aprobada", kind: "info" },
    { value: 3, label: "Vendido", kind: "success" },
    { value: 0, label: "Rechazado", kind: "danger" },
];

const SALE_TYPE_OPTIONS = [
    { value: "credit", label: "Crédito" },
    { value: "cash", label: "Contado" },
];

const FINANCE_OPTIONS_CREDIT = [
    { value: "1", label: "PayJoy" },
    { value: "2", label: "MovilesPay" },
    { value: "3", label: "Efectiva" },
    // CRÉDITO (1) / CELULAR (1) / CREDISMART (4)
    // OJO: segment 4 también existe como MovilPro cuando es CONTADO (2).
    { value: "4", label: "CrediSmart" },
    { value: "5", label: "Alo Credit", color: "#FA8514" },
];

const FINANCE_OPTIONS_CASH = [{ value: "4", label: "MovilPro" }];

const getFinanceLabelByContext = (category, segment) => {
    const cat = String(category ?? "");
    const seg = String(segment ?? "");

    // segment=4 se usa para CrediSmart en crédito y MovilPro en contado.
    if (cat === "2" && seg === "4") return "MovilPro";
    if (cat === "1" && seg === "4") return "CrediSmart";
    if (cat === "1" && seg === "5") return "Alo Credit";

    if (seg === "1") return "PayJoy";
    if (seg === "2") return "MovilesPay";
    if (seg === "3") return "Efectiva";
    return "—";
};

const getFinanceOptionByContext = (category, segment) => {
    const cat = String(category ?? "");
    const seg = String(segment ?? "");

    if (cat === "2") return FINANCE_OPTIONS_CASH[0];
    return (
        FINANCE_OPTIONS_CREDIT.find((x) => String(x.value) === seg) ||
        FINANCE_OPTIONS_CREDIT[0]
    );
};

const RECEIPT_TYPE_OPTIONS = [
    { value: "BOLETA", label: "Boleta" },
    { value: "FACTURA", label: "Factura" },
    { value: "NOTA_VENTA", label: "Nota de venta" },
];

const IGV_AFFECTATION_LABELS = {
    "10": "Gravado - Operación Onerosa",
    "20": "Exonerado - Operación Onerosa",
    "30": "Inafecto - Operación Onerosa",
    "40": "Exportación",
};

const ONLINE_PAY_OPTIONS = [
    { value: "0", label: "No" },
    { value: "1", label: "Yape" },
    { value: "5", label: "Culqi YAPE" },
    { value: "2", label: "Tarjeta" },
    { value: "6", label: "Culqi Tarjeta" },
    { value: "3", label: "Transferencia" },
];

function moneyToNumber(v) {
    const s = String(v ?? "").trim();
    if (!s) return 0;
    const n = Number(String(s).replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
}

function parseMoneyPopup(v) {
    if (v == null) return 0;

    let s = String(v).trim().replace(/\s/g, "");
    s = s.replace(/S\/|s\/|PEN|pen/g, "");
    s = s.replace(/[^\d.,-]/g, "");

    if (!s) return 0;

    const hasDot = s.includes(".");
    const hasComma = s.includes(",");

    if (hasDot && hasComma) {
        const lastDot = s.lastIndexOf(".");
        const lastComma = s.lastIndexOf(",");
        if (lastDot > lastComma) {
            s = s.replace(/,/g, "");
        } else {
            s = s.replace(/\./g, "");
            s = s.replace(/,/g, ".");
        }
    } else if (hasComma && !hasDot) {
        const parts = s.split(",");
        if (parts.length === 2 && parts[1].length <= 2) s = s.replace(/,/g, ".");
        else s = s.replace(/,/g, "");
    } else if (hasDot && !hasComma) {
        const parts = s.split(".");
        if (!(parts.length === 2 && parts[1].length <= 2)) s = s.replace(/\./g, "");
    }

    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
}

function toMoneyUI(v) {
    const n = moneyToNumber(v);
    return n === 0 ? "" : n.toFixed(2);
}

function isEmail(v) {
    const s = String(v || "").trim();
    if (!s) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

function normalizePhone9(v) {
    const d = String(v || "")
        .replace(/\D/g, "")
        .slice(-9);
    return /^9\d{8}$/.test(d) ? d : "";
}

function normalizePeruMobileUI(v, { allowEmpty = false } = {}) {
    const digits = String(v ?? "").replace(/\D/g, "");
    if (!digits) return allowEmpty ? "" : "9";

    let d = digits;
    if (d[0] !== "9") d = "9" + d;
    d = d.slice(0, 9);
    return d;
}

function isPeruCountry(countryIso2) {
    return String(countryIso2 || PERU_ISO2).toLowerCase() === PERU_ISO2;
}

function sanitizeDocumentInput(value, countryIso2) {
    const raw = String(value ?? "");
    if (isPeruCountry(countryIso2)) {
        return raw.replace(/\D/g, "").slice(0, 8);
    }

    return raw.replace(/\D/g, "").slice(0, 9);
}

function getDocumentLabel(countryIso2) {
    return isPeruCountry(countryIso2) ? "DNI" : DEFAULT_DOCUMENT_LABEL;
}

function getDocumentPlaceholder(countryIso2) {
    return isPeruCountry(countryIso2)
        ? "8 dígitos"
        : "9 dígitos";
}

function getCountryDialCode(countryIso2) {
    return (
        COUNTRY_DIAL_CODE_BY_ISO2[String(countryIso2 || "").toLowerCase()] || ""
    );
}

function validateDocumentByCountry(value, countryIso2) {
    const v = String(value || "").trim();
    if (isPeruCountry(countryIso2)) {
        return /^\d{8}$/.test(v);
    }
    return /^\d{9}$/.test(v);
}

function getDocumentErrorMessage(countryIso2) {
    return isPeruCountry(countryIso2)
        ? "DNI inválido (8 dígitos)."
        : "Carnet de extranjería inválido (9 dígitos).";
}

function toNameCase(input) {
    const s = String(input ?? "").trim();
    if (!s) return "";
    const cleaned = s.replace(/\s+/g, " ").toLowerCase();

    return cleaned
        .split(" ")
        .map((w) =>
            w
                .split("-")
                .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : ""))
                .join("-"),
        )
        .join(" ");
}

function sanitizePlainText(input, maxLen = 150) {
    let s = String(input ?? "");
    s = s.replace(/\r\n/g, "\n");
    s = s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "");
    s = s.replace(/[<>]/g, "");
    s = s.replace(/javascript\s*:/gi, "javascript:");
    s = s.replace(/data\s*:/gi, "data:");
    if (maxLen && s.length > maxLen) s = s.slice(0, maxLen);
    return s;
}

function normalizeDescriptionUX(raw) {
    const s = sanitizePlainText(raw, 150);

    const idx = s.indexOf(".");
    if (idx === -1) {
        const trimmedLeft = s.replace(/^\s+/, "");
        if (!trimmedLeft) return s;
        const lower = trimmedLeft.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    }

    const first = s.slice(0, idx);
    const rest = s.slice(idx);

    const firstTrimLeft = first.replace(/^\s+/, "");
    if (!firstTrimLeft) return s;

    const firstLower = firstTrimLeft.toLowerCase();
    const firstSentence =
        firstLower.charAt(0).toUpperCase() + firstLower.slice(1);

    return firstSentence + rest;
}

function requiredLabel(text) {
    return (
        <span className="labelRow">
            <span>{text}</span>
            <span className="reqBadge" title="Obligatorio">
                *
            </span>
        </span>
    );
}

function optionalLabel(text) {
    return (
        <span className="labelRow">
            <span>{text}</span>
            <span className="optBadge" title="Opcional">
                opcional
            </span>
        </span>
    );
}

function formatCardDateTime(value) {
    const m = moment(value).tz(TZ).locale("es");
    if (!m.isValid()) return "";
    return `${m.format("dddd DD [de] MMMM [del] YYYY hh:mm:ss")} ${m.format("A").toUpperCase()}`;
}

function formatShowTextRange(start, end) {
    const startM = moment.tz(start, "YYYY-MM-DD", TZ).locale("es");
    const endM = moment.tz(end, "YYYY-MM-DD", TZ).locale("es");

    if (!startM.isValid() || !endM.isValid())
        return "Mostrando fechas seleccionadas";

    if (startM.isSame(endM, "day")) {
        return `Mostrando ${startM.format("dddd, D [de] MMMM")}`;
    }

    return `Mostrando ${startM.format("dddd, D [de] MMMM")} al ${endM.format("dddd, D [de] MMMM")}`;
}

function isSoldCard(card) {
    return Number(card?.sale_state) === 3;
}

function showSuccessToast(message) {
    try {
        toast.success(message, {
            position: "top-right",
            autoClose: 3000,
            closeOnClick: true,
            theme: "colored",
        });
    } catch (e) {
        console.error("Toast error:", e);
    }
}

function getSuccessToastMessage(state, fmUsed = false) {
    if (state === 1) return "Prospecto actualizado correctamente.";
    if (state === 0) return "Estado actualizado: Rechazado.";
    if (state === 2) return "Estado actualizado: Evaluación aprobada.";
    if (state === 3 && fmUsed) return "Venta y FM registrados correctamente.";
    if (state === 3) return "Venta registrada correctamente.";
    return "Datos registrados correctamente.";
}

export default function Cards() {
    const { user, rol } = useAuth();

    const userRole = Number(String(rol).trim() || 0);
    const isRole3 = userRole === 3;

    const today = useMemo(() => moment().tz(TZ).format("YYYY-MM-DD"), []);

    const [loading, setLoading] = useState(false);
    const [saleCompletion, setSaleCompletion] = useState(null);
    const [cards, setCards] = useState([]);
    const saveStep2Ref = useRef(false);
    const [savingStep2, setSavingStep2] = useState(false);

    const [tabCounts, setTabCounts] = useState({
        0: 0,
        1: 0,
        2: 0,
        3: 0,
        all: 0,
    });

    const [phoneOptions, setPhoneOptions] = useState([]);
    const inventoryRequestSeqRef = useRef(0);
    const activeInventoryCardRef = useRef(null);
    const [inventoryUnitOptions, setInventoryUnitOptions] = useState([]);
    const [inventoryUnitsLoading, setInventoryUnitsLoading] = useState(false);
    const [inventoryUnitsContext, setInventoryUnitsContext] = useState({
        sedeId: null,
        sedeName: "",
    });
    const [advisorsOptions, setAdvisorsOptions] = useState([]);
    const [managersOptions, setManagersOptions] = useState([]);
    const [sedes, setSedes] = useState([]);
    const [sedesFilter, setSedesFilter] = useState([]);
    const [selectedSedeOption, setSelectedSedeOption] = useState(null);

    const [advisorId, setAdvisorId] = useState("0");
    const [sedeId, setSedeId] = useState("0");
    const [sedeName, setSedeName] = useState("Todos");
    const [sedeLocation, setSedeLocation] = useState(
        "Se muestran todas las sedes",
    );
    const [managerId, setManagerId] = useState("0");
    const [saleStateTab, setSaleStateTab] = useState("all");
    const [q, setQ] = useState("");
    const [dateStart, setDateStart] = useState(today);
    const [dateEnd, setDateEnd] = useState(today);
    const [showText, setShowText] = useState(formatShowTextRange(today, today));

    const [showCreate, setShowCreate] = useState(false);
    const [expanded, setExpanded] = useState({});

    const [showFmModal, setShowFmModal] = useState(false);
    const [pendingFmContext, setPendingFmContext] = useState(null);

    const [initialValidationLoading, setInitialValidationLoading] = useState(false);
    const [initialValidation, setInitialValidation] = useState({
        token: "",
        url: "",
        qrCode: "",
        status: "idle",
        completed: false,
        message: "",
        videoUrl: "",
    });

    const [createData, setCreateData] = useState({
        country_iso2: PERU_ISO2,
        country_name: PERU_COUNTRY_NAME,
        country_dial_code: getCountryDialCode(PERU_ISO2),
        dni: "",
        name: "",
        paternalSur: "",
        maternalSur: "",
        email: "",
        phone_client: "9",
        phone_family: "",
        phone_friend: "",
    });
    const [createErrors, setCreateErrors] = useState({});
    const [dniLookupLoading, setDniLookupLoading] = useState(false);
    const [rucLookupLoading, setRucLookupLoading] = useState(false);
    const [rucLookupState, setRucLookupState] = useState({
        status: "idle",
        message: "",
        ruc: "",
    });
    const rucLookupRequestRef = useRef(0);
    const lastRucLookupRef = useRef("");
    const [lastCreatedId, setLastCreatedId] = useState(null);

    const [stepModal, setStepModal] = useState({
        open: false,
        card: null,
        state: null,
    });
    const [stepData, setStepData] = useState({
        country_iso2: PERU_ISO2,
        country_name: PERU_COUNTRY_NAME,
        country_dial_code: getCountryDialCode(PERU_ISO2),
        dni: "",
        name: "",
        paternalSur: "",
        maternalSur: "",
        email: "",
        phone_client: "",
        phone_family: "",
        phone_friend: "",

        sale_type: null,
        finance: null,
        phoneValue: null,
        phone: "",
        inventoryProductId: null,
        inventorySerialId: null,
        inventoryLocationId: null,
        detail_reason: null,
        description: "",
        initial_amount: "",
        future_buy_date: "",
        contract_end_date: "",
        imei: "",
        phonePrice: "",
        fm: { value: "0", label: "No" },
        fmAmount: "",
        cashAmount: "",
        onlinePay: ONLINE_PAY_OPTIONS[0],
        onlineAmount: "",
        manager_id: null,
        receiptType: RECEIPT_TYPE_OPTIONS[0],
        customerRuc: "",
        customerBusinessName: "",
        customerAddress: "",
    });
    const [stepErrors, setStepErrors] = useState({});
    const [selectedPromoGifts,setSelectedPromoGifts] = useState([]);
    const onPromoGiftsChange = React.useCallback((items)=>setSelectedPromoGifts(items),[]);

    // Flujo de revisión previa reutilizable. El mismo popup se usa para:
    // - confirmar datos del nuevo prospecto antes de crearlo;
    // - revisar paso a paso una venta al contado o a crédito antes de guardarla.
    // Estas confirmaciones son de UX y no alteran el payload tributario ni de inventario.
    const [saleReviewPopup, setSaleReviewPopup] = useState({
        open: false,
        context: null,
        step: 0,
    });
    const [pendingCreatePayload, setPendingCreatePayload] = useState(null);

    const [managerModal, setManagerModal] = useState({ open: false, card: null });
    const [selectedManager, setSelectedManager] = useState(null);

    const futureDateRef = useRef(null);
    const futureFpInstance = useRef(null);
    const contractEndDateRef = useRef(null);
    const contractEndFpInstance = useRef(null);

    const createCardRef = useRef(null);
    const shouldAutoScrollCreateRef = useRef(false);

    const calendarInputRef = useRef(null);
    const calendarInstanceRef = useRef(null);

    const api = useMemo(
        () =>
            axios.create({
                baseURL: process.env.REACT_APP_API,
                timeout: 30000,
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            }),
        [],
    );

    const reactSelectStyles = useMemo(
        () => ({
            control: (base, state) => ({
                ...base,
                minHeight: 40,
                borderColor: state.isFocused ? base.borderColor : base.borderColor,
                boxShadow: state.isFocused ? base.boxShadow : "none",
            }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }),
        [],
    );

    const countryOptions = useMemo(() => {
        const names = countries.getNames("es", { select: "official" }) || {};

        const list = Object.entries(names)
            .map(([iso2, label]) => ({
                value: String(iso2 || "").toLowerCase(),
                label: String(label || "").trim(),
            }))
            .filter((item) => item.value && item.label);

        const normalized = [];
        const seen = new Set();
        const peru = list.find((item) => item.value === PERU_ISO2) || {
            value: PERU_ISO2,
            label: PERU_COUNTRY_NAME,
        };

        normalized.push({
            ...peru,
            label: PERU_COUNTRY_NAME,
            dial_code: getCountryDialCode(PERU_ISO2),
        });
        seen.add(PERU_ISO2);

        list
            .filter((item) => item.value !== PERU_ISO2)
            .sort((a, b) =>
                a.label.localeCompare(b.label, "es", { sensitivity: "base" }),
            )
            .forEach((item) => {
                if (seen.has(item.value)) return;
                seen.add(item.value);
                normalized.push({
                    ...item,
                    dial_code: getCountryDialCode(item.value),
                });
            });

        return normalized;
    }, []);

    const getCountryOption = (countryIso2) => {
        const iso2 = String(countryIso2 || PERU_ISO2).toLowerCase();
        return (
            countryOptions.find((option) => option.value === iso2) ||
            countryOptions[0] ||
            null
        );
    };

    // Catálogo legado: se conserva para no romper flujos existentes que aún consumen /cards/meta.
    const phoneSelectOptions = useMemo(() => {
        return phoneOptions.map((o) => ({
            value: String(o.value),
            label: o.label,
        }));
    }, [phoneOptions]);

    const buildInventoryUnitOption = (unit) => ({
        value: String(unit.serialId),
        label: `${unit.title} · IMEI ${unit.imei}`,
        raw: unit,
    });
    const advisorFilterOptions = useMemo(() => {
        return [{ value: "0", label: "Todos" }, ...advisorsOptions];
    }, [advisorsOptions]);

    const managerFilterOptions = useMemo(() => {
        return [{ value: "0", label: "Todos" }, ...managersOptions];
    }, [managersOptions]);

    const selectedAdvisorOption = useMemo(() => {
        return (
            advisorFilterOptions.find(
                (option) => String(option?.value) === String(advisorId),
            ) ||
            advisorFilterOptions[0] ||
            null
        );
    }, [advisorFilterOptions, advisorId]);

    const selectedManagerOption = useMemo(() => {
        return (
            managerFilterOptions.find(
                (option) => String(option?.value) === String(managerId),
            ) ||
            managerFilterOptions[0] ||
            null
        );
    }, [managerFilterOptions, managerId]);

    const selectedCreateCountryOption = useMemo(
        () => getCountryOption(createData.country_iso2),
        [countryOptions, createData.country_iso2],
    );
    const selectedStepCountryOption = useMemo(
        () => getCountryOption(stepData.country_iso2),
        [countryOptions, stepData.country_iso2],
    );

    const headerFilterSelectStyles = useMemo(
        () => ({
            ...reactSelectStyles,
            control: (base, state) => ({
                ...base,
                minHeight: 46,
                borderRadius: 14,
                borderColor: state.isFocused
                    ? "rgba(77, 0, 132, 0.42)"
                    : "rgba(203, 213, 225, 0.95)",
                boxShadow: state.isFocused
                    ? "0 0 0 4px rgba(77, 0, 132, 0.10)"
                    : "0 4px 14px rgba(15, 23, 42, 0.04)",
                background: "#fff",
                transition:
                    "border-color .18s ease, box-shadow .18s ease, transform .18s ease",
                paddingLeft: 2,
            }),
            valueContainer: (base) => ({
                ...base,
                padding: "4px 10px",
            }),
            placeholder: (base) => ({
                ...base,
                color: "#64748b",
                fontSize: 13,
            }),
            singleValue: (base) => ({
                ...base,
                color: "#0f172a",
                fontSize: 13,
                fontWeight: 600,
            }),
            input: (base) => ({
                ...base,
                color: "#0f172a",
                fontSize: 13,
            }),
            indicatorSeparator: (base) => ({
                ...base,
                backgroundColor: "rgba(203, 213, 225, 0.95)",
            }),
            dropdownIndicator: (base, state) => ({
                ...base,
                color: state.isFocused ? "#4d0084" : "#64748b",
            }),
            menu: (base) => ({
                ...base,
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(226, 232, 240, 0.95)",
                boxShadow: "0 16px 42px rgba(15, 23, 42, 0.14)",
            }),
            menuList: (base) => ({
                ...base,
                paddingTop: 8,
                paddingBottom: 8,
            }),
            option: (base, state) => {
                const isAloCredit = String(state.data?.value || "") === "5";
                return {
                    ...base,
                    fontSize: 13,
                    fontWeight: state.isSelected ? 800 : 600,
                    color: isAloCredit ? "#FA8514" : state.isSelected ? "#4d0084" : "#1e293b",
                    backgroundColor: state.isSelected
                        ? isAloCredit ? "rgba(250, 133, 20, 0.12)" : "rgba(77, 0, 132, 0.10)"
                        : state.isFocused
                            ? isAloCredit ? "rgba(250, 133, 20, 0.08)" : "rgba(77, 0, 132, 0.06)"
                            : "#fff",
                    cursor: "pointer",
                };
            },
            groupHeading: (base) => ({
                ...base,
                margin: 0,
                padding: 0,
            }),
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }),
        [reactSelectStyles],
    );

    const findOptionInGrouped = (opts, value) => {
        const v = String(value);
        for (const item of opts || []) {
            if (item?.options && Array.isArray(item.options)) {
                const hit = item.options.find((o) => String(o?.value) === v);
                if (hit) return hit;
            } else if (String(item?.value) === v) {
                return item;
            }
        }
        return null;
    };

    const isMobileViewport = () => {
        if (typeof window === "undefined") return false;
        return window.matchMedia("(max-width: 768px)").matches;
    };

    const openCreateCard = () => {
        shouldAutoScrollCreateRef.current = isMobileViewport();
        setShowCreate(true);
    };

    useEffect(() => {
        if (!showCreate || !shouldAutoScrollCreateRef.current) return undefined;

        const timer = window.setTimeout(() => {
            createCardRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
            shouldAutoScrollCreateRef.current = false;
        }, 120);

        return () => window.clearTimeout(timer);
    }, [showCreate]);

    const tabs = useMemo(
        () => [
            { key: "1", label: "Venta pendiente" },
            { key: "2", label: "Evaluación aprobada" },
            { key: "3", label: "Vendido" },
            { key: "0", label: "Rechazado" },
            { key: "all", label: "Todos" },
        ],
        [],
    );

    const onChangeTab = async (key) => {
        setSaleStateTab(String(key));
        setLoading(true);
        try {
            await fetchCards({ sale_state: String(key) });
        } finally {
            setLoading(false);
        }
    };

    const onAdvisorChange = async (val) => {
        const nextAdvisorId = String(val);
        setAdvisorId(nextAdvisorId);
        setSedeId("0");
        setSelectedSedeOption(null);
        setSedeName("Todos");
        setSedeLocation("Se muestran todas las sedes");
        setLoading(true);
        try {
            await fetchCards({ advisor_id: nextAdvisorId, sede_id: "0" });
        } finally {
            setLoading(false);
        }
    };

    const onSedeChange = async (option) => {
        const nextSedeId = String(option?.value ?? "0");
        setSedeId(nextSedeId);
        setSelectedSedeOption(option || null);

        let filterSede = (sedes || []).find(
            (item) => String(item?.idNumber) === nextSedeId,
        );
        if (!filterSede) {
            filterSede = {
                sede: "Todos",
                sedeLocation: "Se muestran todas las sedes",
            };
        }
        setSedeName(String(filterSede?.sede || "Todos"));
        setSedeLocation(
            String(filterSede?.sedeLocation || "Se muestran todas las sedes"),
        );

        setLoading(true);
        try {
            await fetchCards({ sede_id: nextSedeId });
        } finally {
            setLoading(false);
        }
    };

    const onManagerChange = async (val) => {
        setManagerId(String(val));
        setLoading(true);
        try {
            await fetchCards({ manager_id: String(val) });
        } finally {
            setLoading(false);
        }
    };

    async function fetchMeta(overrides = {}) {
        const params = {
            start: overrides.start ?? dateStart,
            end: overrides.end ?? dateEnd,
            sale_state: overrides.sale_state ?? saleStateTab,
            advisor_id: overrides.advisor_id ?? advisorId,
            manager_id: overrides.manager_id ?? managerId,
            q: overrides.q ?? q,
        };

        const res = await api.get("/cards/meta", { params });
        const data = res?.data || {};
        const phones = data?.phones || [];
        const advisors = data?.advisors || [];
        const managers = data?.managers || [];
        const sedesList = data?.sedes || [];
        const totalCards = Number(data?.meta?.totalCards || 0);

        setPhoneOptions(phones);
        setAdvisorsOptions(
            advisors.map((a) => ({ value: String(a.value), label: a.label })),
        );
        setManagersOptions(
            managers.map((m) => ({ value: String(m.value), label: m.label })),
        );
        setSedes(sedesList);

        const groupedOptions = buildGroupedSedesOptions(sedesList, totalCards);
        setSedesFilter(groupedOptions);

        const selectedValue = String(
            overrides.selected_sede_id ?? overrides.sede_id ?? sedeId ?? "0",
        );
        const nextSelected =
            findOptionInGrouped(groupedOptions, selectedValue) ||
            findOptionInGrouped(groupedOptions, 0);
        setSelectedSedeOption(nextSelected || null);

        let filterSede = sedesList.find(
            (item) => String(item?.idNumber) === selectedValue,
        );
        if (!filterSede) {
            filterSede = {
                sede: "Todos",
                sedeLocation: "Se muestran todas las sedes",
            };
        }
        setSedeName(String(filterSede?.sede || "Todos"));
        setSedeLocation(
            String(filterSede?.sedeLocation || "Se muestran todas las sedes"),
        );
    }

    async function fetchCards(overrides = {}) {
        const params = {
            start: overrides.start ?? dateStart,
            end: overrides.end ?? dateEnd,
            sale_state: overrides.sale_state ?? saleStateTab,
            advisor_id: overrides.advisor_id ?? advisorId,
            sede_id: overrides.sede_id ?? sedeId,
            manager_id: overrides.manager_id ?? managerId,
            q: overrides.q ?? q,
        };

        const res = await api.get("/cards/list", { params });
        const list = res?.data?.cards || [];
        setCards(list);

        try {
            await fetchMeta({ ...params, selected_sede_id: params.sede_id });
        } catch (e) {
            console.warn("cards.meta refresh error:", e);
        }

        try {
            const wantsAll = String(params.sale_state) === "all";
            const resAll = wantsAll
                ? res
                : await api.get("/cards/list", {
                    params: { ...params, sale_state: "all" },
                });
            const allList = resAll?.data?.cards || [];

            const counts = { 0: 0, 1: 0, 2: 0, 3: 0, all: allList.length };
            for (const r of allList) {
                const k = String(r?.sale_state ?? "");
                if (counts[k] !== undefined) counts[k] += 1;
            }
            setTabCounts(counts);
        } catch (e) {
            console.warn("cards.tabCounts error:", e);
        }
    }

    const renderDetail = (label, value, formatFn) => {
        if (value === null || value === undefined || value === "") return null;

        const finalValue = formatFn ? formatFn(value) : value;
        if (finalValue === null || finalValue === undefined || finalValue === "")
            return null;

        return (
            <div>
                <div className="detailK">{label}</div>
                <div className="detailV">{finalValue}</div>
            </div>
        );
    };

    useEffect(() => {
        moment.tz.setDefault(TZ);
        moment.locale("es");
        Flatpickr.localize(Spanish);

        (async () => {
            setLoading(true);
            try {
                await fetchCards({
                    start: today,
                    end: today,
                    sale_state: "all",
                    q: "",
                    sede_id: "0",
                });
            } catch (e) {
                console.error(e);
                alert(e?.response?.data?.error || "Error cargando Cards");
            } finally {
                setLoading(false);
            }
        })();

        const input = calendarInputRef.current;
        const franj = 0;
        if (!isRole3 && input && !calendarInstanceRef.current) {
            calendarInstanceRef.current = Flatpickr(input, {
                mode: "range",
                dateFormat: "Y-m-d",
                defaultDate: [today, today],
                allowInput: true,
                ...(userRole ? { minDate: "2026-03-11" } : {}),
                onChange: (selectedDates) => {
                    if (!selectedDates || selectedDates.length === 0) {
                        setSaleStateTab("all");
                        setShowText(formatShowTextRange(today, today));
                        setDateStart(today);
                        setDateEnd(today);
                        fetchCards({ start: today, end: today, sale_state: "all" });
                        return;
                    }

                    if (selectedDates.length === 1) {
                        const d = moment(selectedDates[0]).tz(TZ).format("YYYY-MM-DD");
                        setSaleStateTab("all");
                        setShowText(formatShowTextRange(d, d));
                        setDateStart(d);
                        setDateEnd(d);
                        fetchCards({ start: d, end: d, sale_state: "all" });
                        return;
                    }

                    const d1 = moment(selectedDates[0]).tz(TZ).format("YYYY-MM-DD");
                    const d2 = moment(selectedDates[1]).tz(TZ).format("YYYY-MM-DD");

                    const start = d1 <= d2 ? d1 : d2;
                    const end = d1 <= d2 ? d2 : d1;

                    setSaleStateTab("all");
                    setShowText(formatShowTextRange(start, end));
                    setDateStart(start);
                    setDateEnd(end);
                    fetchCards({ start, end, sale_state: "all" });
                },
            });
        }

        return () => {
            try {
                calendarInstanceRef.current?.destroy?.();
            } catch {}
            calendarInstanceRef.current = null;
        };
    }, []);

    useEffect(() => {
        const text = String(q || "").trim();
        if (text.length === 0) {
            fetchCards({ q: "" });
            return;
        }
        if (text.length < 3) return;

        const t = setTimeout(() => {
            fetchCards({ q: text });
        }, 350);
        return () => clearTimeout(t);
    }, [q]);

    useEffect(() => {
        if (!stepModal.open) return;

        if (futureDateRef.current) {
            if (futureFpInstance.current) {
                futureFpInstance.current.destroy();
                futureFpInstance.current = null;
            }
            futureFpInstance.current = Flatpickr(futureDateRef.current, {
                dateFormat: "Y-m-d",
                minDate: "today",
                locale: Spanish,
                onChange: (selectedDates, dateStr) => {
                    setStep({ future_buy_date: dateStr });
                },
                defaultDate: stepData.future_buy_date ? stepData.future_buy_date : null,
            });
        }
        return () => {
            if (futureFpInstance.current) {
                futureFpInstance.current.destroy();
                futureFpInstance.current = null;
            }
        };
    }, [stepModal.open, stepData.detail_reason?.value]);

    useEffect(() => {
        if (!stepModal.open || Number(stepModal.state) !== 3) return undefined;

        const styleId = "cardsContractEndFlatpickrStyles";
        if (typeof document !== "undefined" && !document.getElementById(styleId)) {
            const style = document.createElement("style");
            style.id = styleId;
            style.innerHTML = `
                .flatpickr-day.cardsContractMilestoneDay {
                    position: relative;
                    border-color: rgba(77, 0, 132, 0.72) !important;
                    color: #4d0084 !important;
                    font-weight: 900 !important;
                    background: linear-gradient(135deg, rgba(77, 0, 132, 0.13), rgba(255, 255, 255, 0.96)) !important;
                    box-shadow: inset 0 0 0 2px rgba(77, 0, 132, 0.14);
                }
                .flatpickr-day.cardsContractMilestoneDay::after {
                    content: "";
                    width: 6px;
                    height: 6px;
                    border-radius: 999px;
                    background: #4d0084;
                    position: absolute;
                    left: 50%;
                    bottom: 4px;
                    transform: translateX(-50%);
                }
                .flatpickr-day.cardsContractMilestoneSelected {
                    background: #4d0084 !important;
                    color: #fff !important;
                    border-color: #4d0084 !important;
                }
                .flatpickr-day.cardsContractMilestoneSelected::after {
                    background: #fff;
                }
            `;
            document.head.appendChild(style);
        }

        if (contractEndDateRef.current) {
            if (contractEndFpInstance.current) {
                contractEndFpInstance.current.destroy();
                contractEndFpInstance.current = null;
            }

            const baseDate = moment().tz(TZ).startOf("day");
            const maxContractDate = baseDate
                .clone()
                .add(1, "year")
                .format("YYYY-MM-DD");
            const milestoneMap = new Map([
                [baseDate.clone().add(3, "months").format("YYYY-MM-DD"), "3 meses"],
                [baseDate.clone().add(6, "months").format("YYYY-MM-DD"), "6 meses"],
                [baseDate.clone().add(9, "months").format("YYYY-MM-DD"), "9 meses"],
            ]);

            contractEndFpInstance.current = Flatpickr(contractEndDateRef.current, {
                locale: Spanish,
                dateFormat: "Y-m-d",
                allowInput: false,
                minDate: "today",
                maxDate: maxContractDate,
                defaultDate: stepData.contract_end_date || null,
                onDayCreate: (_dObj, _dStr, _fp, dayElem) => {
                    const dateKey = moment(dayElem.dateObj).format("YYYY-MM-DD");
                    const milestoneLabel = milestoneMap.get(dateKey);
                    if (!milestoneLabel) return;

                    dayElem.classList.add("cardsContractMilestoneDay");
                    dayElem.setAttribute("title", `Contrato a ${milestoneLabel}`);

                    if (String(stepData.contract_end_date || "") === dateKey) {
                        dayElem.classList.add("cardsContractMilestoneSelected");
                    }
                },
                onChange: (_selectedDates, dateStr) => {
                    setStep({ contract_end_date: dateStr });
                },
            });
        }

        return () => {
            if (contractEndFpInstance.current) {
                contractEndFpInstance.current.destroy();
                contractEndFpInstance.current = null;
            }
        };
    }, [stepModal.open, stepModal.state]);

    function setCreate(patch) {
        setCreateData((prev) => ({ ...prev, ...patch }));
    }

    function setStep(patch) {
        setStepData((prev) => ({ ...prev, ...patch }));

        // Si el usuario cambia uno de los datos que se revisan en los popups,
        // la secuencia vuelve a empezar para que la confirmación corresponda
        // exactamente a los valores actuales del formulario.
        const reviewSensitiveFields = [
            "sale_type",
            "finance",
            "phoneValue",
            "phone",
            "imei",
            "phonePrice",
            "cashAmount",
            "onlinePay",
            "onlineAmount",
            "fm",
            "fmAmount",
            "receiptType",
            "customerRuc",
            "customerBusinessName",
            "customerAddress",
        ];
        if (reviewSensitiveFields.some((field) => Object.prototype.hasOwnProperty.call(patch, field))) {
            setSaleReviewPopup({ open: false, context: null, step: 0 });
        }
    }

    function applyCountryToCreate(option) {
        const next = option || getCountryOption(PERU_ISO2);
        const nextIso2 = String(next?.value || PERU_ISO2).toLowerCase();

        setCreate({
            country_iso2: nextIso2,
            country_name: String(next?.label || PERU_COUNTRY_NAME),
            country_dial_code: String(
                next?.dial_code || getCountryDialCode(nextIso2),
            ),
            dni: "",
            name: "",
            paternalSur: "",
            maternalSur: "",
        });

        setCreateErrors((prev) => ({
            ...prev,
            dni: undefined,
            name: undefined,
            paternalSur: undefined,
            maternalSur: undefined,
        }));
    }

    function applyCountryToStep(option) {
        const next = option || getCountryOption(PERU_ISO2);
        const nextIso2 = String(next?.value || PERU_ISO2).toLowerCase();

        setStep((prev) => ({
            ...prev,
            country_iso2: nextIso2,
            country_name: String(next?.label || PERU_COUNTRY_NAME),
            country_dial_code: String(
                next?.dial_code || getCountryDialCode(nextIso2),
            ),
            dni: "",
            name: "",
            paternalSur: "",
            maternalSur: "",
        }));

        setStepErrors((prev) => ({
            ...prev,
            dni: undefined,
            name: undefined,
            paternalSur: undefined,
            maternalSur: undefined,
        }));
    }

    async function lookupDni(dni, target = "create", countryIso2 = PERU_ISO2) {
        try {
            const d = String(dni || "").trim();
            if (!isPeruCountry(countryIso2) || !/^\d{8}$/.test(d) || !DNI_API_BEARER)
                return;

            setDniLookupLoading(true);

            const res = await axiosDni.post("/", { dni: d });
            const r = res?.data?.data || res?.data || {};
            const nombres = r?.nombres || r?.name || "";
            const apPat =
                r?.apellido_paterno || r?.apellidoPaterno || r?.paterno || "";
            const apMat =
                r?.apellido_materno || r?.apellidoMaterno || r?.materno || "";

            const patch = {};
            if (nombres) patch.name = toNameCase(nombres);
            if (apPat) patch.paternalSur = toNameCase(apPat);
            if (apMat) patch.maternalSur = toNameCase(apMat);

            if (!Object.keys(patch).length) return;
            if (target === "step") setStep(patch);
            else setCreate(patch);
        } catch (e) {
            console.warn("DNI lookup failed:", e?.response?.data || e?.message);
        } finally {
            setDniLookupLoading(false);
        }
    }

    function resetRucLookupState() {
        rucLookupRequestRef.current += 1;
        lastRucLookupRef.current = "";
        setRucLookupLoading(false);
        setRucLookupState({ status: "idle", message: "", ruc: "" });
    }

    async function lookupCustomerRuc(ruc, { force = false } = {}) {
        const normalizedRuc = String(ruc || "").replace(/\D/g, "").slice(0, 11);
        if (stepData.receiptType?.value !== "FACTURA" || !/^\d{11}$/.test(normalizedRuc)) {
            return;
        }

        if (!RUC_API_BEARER) {
            setRucLookupState({
                status: "error",
                message: "No se encontró REACT_APP_APIPERU_TOKEN para consultar el RUC.",
                ruc: normalizedRuc,
            });
            return;
        }

        if (!force && lastRucLookupRef.current === normalizedRuc) return;

        const requestId = rucLookupRequestRef.current + 1;
        rucLookupRequestRef.current = requestId;
        lastRucLookupRef.current = normalizedRuc;
        setRucLookupLoading(true);
        setRucLookupState({
            status: "loading",
            message: "Consultando RUC...",
            ruc: normalizedRuc,
        });
        setStepErrors((prev) => ({
            ...prev,
            customerRuc: undefined,
            customerBusinessName: undefined,
            customerAddress: undefined,
        }));

        try {
            const res = await axiosRuc.post("/", { ruc: normalizedRuc });
            if (requestId !== rucLookupRequestRef.current) return;

            const result = res?.data?.data || res?.data || {};
            const businessName = String(
                result?.razon_social ||
                result?.razonSocial ||
                result?.nombre_o_razon_social ||
                result?.nombre ||
                ""
            ).replace(/\s+/g, " ").trim();

            const address = String(
                result?.direccion ||
                result?.direccion_fiscal ||
                result?.domicilio_fiscal ||
                result?.domicilioFiscal ||
                ""
            ).replace(/\s+/g, " ").trim();

            if (!businessName) {
                throw new Error("No se encontraron datos para el RUC ingresado.");
            }

            setStepData((current) => {
                if (String(current.customerRuc || "") !== normalizedRuc) return current;
                return {
                    ...current,
                    customerBusinessName: businessName,
                    customerAddress: address,
                };
            });

            setRucLookupState({
                status: "success",
                message: address
                    ? "Datos del RUC autocompletados correctamente."
                    : "Razón social encontrada. Completa o verifica la dirección fiscal.",
                ruc: normalizedRuc,
            });
        } catch (error) {
            if (requestId !== rucLookupRequestRef.current) return;
            lastRucLookupRef.current = "";

            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.response?.data?.msg ||
                error?.message ||
                "No se pudo consultar el RUC. Puedes completar los datos manualmente.";

            setStepData((current) => {
                if (String(current.customerRuc || "") !== normalizedRuc) return current;
                return {
                    ...current,
                    customerBusinessName: "",
                    customerAddress: "",
                };
            });

            setRucLookupState({ status: "error", message, ruc: normalizedRuc });
        } finally {
            if (requestId === rucLookupRequestRef.current) setRucLookupLoading(false);
        }
    }

    function validateCreate(payload) {
        const err = {};
        if (!validateDocumentByCountry(payload.dni, payload.country_iso2))
            err.dni = getDocumentErrorMessage(payload.country_iso2);
        if (!String(payload.name || "").trim())
            err.name = "Nombres es obligatorio.";
        if (!String(payload.paternalSur || "").trim())
            err.paternalSur = "Apellido paterno es obligatorio.";
        if (!String(payload.maternalSur || "").trim())
            err.maternalSur = "Apellido materno es obligatorio.";
        if (isPeruCountry(payload.country_iso2)) {
            const pClient = normalizePhone9(payload.phone_client);
            if (!pClient)
                err.phone_client = "Celular del cliente es obligatorio (9 dígitos).";
            if (payload.phone_family && !normalizePhone9(payload.phone_family))
                err.phone_family = "Celular 2 inválido.";
            if (payload.phone_friend && !normalizePhone9(payload.phone_friend))
                err.phone_friend = "Celular 3 inválido.";
        }
        return err;
    }

    async function persistCreatePayload(payload) {
        setLoading(true);
        try {
            const res = await api.post("/cards/create", payload);
            if (res?.data?.msg === "success") {
                setLastCreatedId(res?.data?.id || null);
                setShowCreate(false);
                setCreateData({
                    country_iso2: PERU_ISO2,
                    country_name: PERU_COUNTRY_NAME,
                    country_dial_code: getCountryDialCode(PERU_ISO2),
                    dni: "",
                    name: "",
                    paternalSur: "",
                    maternalSur: "",
                    email: "",
                    phone_client: "9",
                    phone_family: "",
                    phone_friend: "",
                });
                setCreateErrors({});
                setPendingCreatePayload(null);
                showSuccessToast("Prospecto registrado correctamente.");
                await fetchCards();
            } else {
                alert(res?.data?.error || "No se pudo guardar el prospecto.");
            }
        } catch (e) {
            console.error(e);
            alert(e?.response?.data?.error || "Error guardando prospecto");
        } finally {
            setLoading(false);
        }
    }

    async function onCreateSave() {
        const payload = {
            country_iso2: String(createData.country_iso2 || PERU_ISO2).toLowerCase(),
            country_name: String(createData.country_name || PERU_COUNTRY_NAME).trim(),
            country_dial_code: String(
                createData.country_dial_code ||
                getCountryDialCode(createData.country_iso2),
            ).trim(),
            dni: String(createData.dni || "").trim(),
            name: toNameCase(createData.name),
            paternalSur: toNameCase(createData.paternalSur),
            maternalSur: toNameCase(createData.maternalSur),
            email: String(createData.email || "").trim(),
            phone_client: isPeruCountry(createData.country_iso2)
                ? normalizePhone9(createData.phone_client)
                : String(createData.phone_client || "").trim(),
            phone_family: isPeruCountry(createData.country_iso2)
                ? normalizePhone9(createData.phone_family)
                : String(createData.phone_family || "").trim(),
            phone_friend: isPeruCountry(createData.country_iso2)
                ? normalizePhone9(createData.phone_friend)
                : String(createData.phone_friend || "").trim(),
        };

        const err = validateCreate(payload);
        setCreateErrors(err);
        if (Object.keys(err).length) return;

        // Antes de crear el prospecto se confirma DNI + nombre completo usando
        // exactamente el mismo componente visual que las revisiones de venta.
        setPendingCreatePayload(payload);
        setSaleReviewPopup({ open: true, context: "prospect", step: 1 });
    }

    useEffect(() => {
        if (!lastCreatedId) return;
        const t = setTimeout(() => {
            const el = document.getElementById(`card-${lastCreatedId}`);
            if (el) {
                el.classList.add("justCreated");
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                setTimeout(() => el.classList.remove("justCreated"), 2400);
            }
        }, 120);
        return () => clearTimeout(t);
    }, [cards, lastCreatedId]);

    function parseSaleMeta(card) {
        try {
            if (!card?.sale_meta) return null;
            if (typeof card.sale_meta === "object") return card.sale_meta || null;
            const parsed = JSON.parse(String(card.sale_meta));
            return parsed && typeof parsed === "object" ? parsed : null;
        } catch {
            return null;
        }
    }

    function getCardCountryName(card) {
        const metaObj = parseSaleMeta(card);
        const countryName = String(metaObj?.country_name || "").trim();
        return countryName && countryName !== PERU_COUNTRY_NAME ? countryName : "";
    }

    function buildReasonOptions(state, finance) {
        const financeName = finance?.label || "la fuente";
        if (state === 0) {
            return [
                { value: "INFOCORP", label: "Cliente en Infocorp" },
                {
                    value: "BAD_HISTORY",
                    label: "Cliente no tiene historial crediticio",
                },
                { value: "NOT_APPROVED", label: `Cliente no aprobó en ${financeName}` },
            ];
        }
        if (state === 2) {
            return [
                {
                    value: "NO_INITIAL",
                    label: "Cliente no cuenta con el monto inicial",
                },
                { value: "NO_STOCK", label: "No hay stock del modelo del equipo" },
                {
                    value: "BUY_LATER",
                    label: "Cliente comprará otro día (fin de mes / cuando cobre)",
                },
            ];
        }
        return [];
    }

    function getReasonLabelByValue(state, finance, value) {
        const opts = buildReasonOptions(state, finance);
        return opts.find((o) => String(o.value) === String(value))?.label || "";
    }

    function getReasonLabelFromMeta(card, metaObj) {
        const cardState = Number(card?.sale_state);
        const metaState = Number(metaObj?.state);
        const stateResolved =
            Number.isFinite(metaState) && [0, 1, 2, 3].includes(metaState)
                ? metaState
                : cardState;

        const financeVal = getFinanceOptionByContext(
            card?.category,
            card?.segment || "1",
        );

        if (metaObj?.detail_reason_label) {
            return String(metaObj.detail_reason_label);
        }

        if (metaObj?.detail_reason) {
            return (
                getReasonLabelByValue(
                    stateResolved,
                    financeVal,
                    metaObj.detail_reason,
                ) || String(metaObj.detail_reason)
            );
        }

        return "";
    }

    function formatMetaDate(dateStr) {
        const s = String(dateStr || "").trim();
        if (!s) return "";
        const m = moment(s, ["YYYY-MM-DD", moment.ISO_8601], true);
        return m.isValid() ? m.format("DD/MM/YYYY") : s;
    }

    const getPublicBaseUrl = () => {
        const candidate =
            process.env.REACT_APP_PUBLIC_FRONT_URL ||
            process.env.REACT_APP_FRONT_URL ||
            (typeof window !== "undefined" ? window.location.origin : "");
        return String(candidate || "").replace(/\/$/, "");
    };

    const isStepCreditSale = () => stepData.sale_type?.value !== "cash";

    const getInitialTotalAmount = () => {
        const cash = moneyToNumber(stepData.cashAmount);
        const onlinePay = String(stepData.onlinePay?.value || "0");
        const online = onlinePay === "0" ? 0 : moneyToNumber(stepData.onlineAmount);
        const fm =
            isStepCreditSale() && String(stepData.fm?.value || "0") === "1"
                ? moneyToNumber(stepData.fmAmount)
                : 0;
        return cash + online + fm;
    };

    function resetInitialValidationState() {
        setInitialValidation({
            token: "",
            url: "",
            qrCode: "",
            status: "idle",
            completed: false,
            message: "",
            videoUrl: "",
        });
    }

    async function createInitialValidationQr() {
        const cardId = stepModal.card?.id;
        const dni = String(stepData.dni || "").trim();
        const initialAmount = getInitialTotalAmount();

        if (!cardId) return;
        if (!validateDocumentByCountry(dni, stepData.country_iso2)) {
            setStepErrors((prev) => ({ ...prev, dni: getDocumentErrorMessage(stepData.country_iso2) }));
            return;
        }
        if (!(initialAmount > 0)) {
            setStepErrors((prev) => ({
                ...prev,
                initial_validation: "Ingresa primero el monto de inicial en efectivo, online o FM.",
            }));
            return;
        }

        setInitialValidationLoading(true);
        try {
            const fullName = `${stepData.name || ""} ${stepData.paternalSur || ""} ${stepData.maternalSur || ""}`
                .replace(/\s+/g, " ")
                .trim();
            const { data } = await api.post(`/cards/${cardId}/initial-validation`, {
                dni,
                fullName,
                initialAmount,
                frontUrl: getPublicBaseUrl(),
            });

            if (data?.msg !== "success") throw new Error(data?.error || "No se pudo generar QR.");
            setInitialValidation({
                token: data.data.token,
                url: data.data.url,
                qrCode: data.data.qrCode,
                status: data.data.status || "pending",
                completed: false,
                message: "QR generado. Escanéalo para grabar la confirmación de inicial.",
                videoUrl: "",
            });
            setStepErrors((prev) => ({ ...prev, initial_validation: undefined }));
        } catch (e) {
            alert(e?.response?.data?.error || e?.message || "No se pudo generar el QR de inicial.");
        } finally {
            setInitialValidationLoading(false);
        }
    }

    async function verifyInitialValidation() {
        const cardId = stepModal.card?.id;
        if (!cardId) return;

        setInitialValidationLoading(true);
        try {
            const { data } = await api.get(`/cards/${cardId}/initial-validation/status`);
            const validation = data?.data?.validation || null;
            const completed = data?.data?.completed === true;
            setInitialValidation((old) => ({
                ...old,
                token: validation?.token || old.token || "",
                status: completed ? "completed" : validation?.status || old.status || "pending",
                completed,
                videoUrl: validation?.videoUrl || old.videoUrl || "",
                message: completed
                    ? "Validación de inicial completada. Ya puedes guardar el registro."
                    : "La validación aún está pendiente. Verifica después de que el cliente envíe el video.",
            }));
            if (completed) setStepErrors((prev) => ({ ...prev, initial_validation: undefined }));
            return completed;
        } catch (e) {
            alert(e?.response?.data?.error || "No se pudo verificar la validación.");
            return false;
        } finally {
            setInitialValidationLoading(false);
        }
    }

    async function loadInventoryUnitsForCard(card, preferred = {}, preserveSelection = false) {
        const requestSeq = ++inventoryRequestSeqRef.current;
        const cardId = Number(card?.id || 0);
        if (!cardId) {
            setInventoryUnitOptions([]);
            setInventoryUnitsContext({ sedeId: null, sedeName: "" });
            return [];
        }

        setInventoryUnitsLoading(true);
        try {
            const { data } = await api.get(`/cards/${cardId}/inventory-products`, {
                params: { _inventoryRefresh: Date.now() },
                headers: { "Cache-Control": "no-cache" },
            });
            if (requestSeq !== inventoryRequestSeqRef.current || activeInventoryCardRef.current !== cardId) return [];
            const units = Array.isArray(data?.data?.units) ? data.data.units : [];
            const options = units.map(buildInventoryUnitOption);
            setInventoryUnitOptions(options);
            setInventoryUnitsContext({
                sedeId: data?.data?.sedeId || card?.fk_sede_id || null,
                sedeName: data?.data?.sedeName || card?.sede_name || "",
            });

            const preferredSerialId = String(
                preferred.inventorySerialId || preferred.inventory_serial_id || "",
            );
            const preferredImei = String(preferred.imei || card?.imei || "").trim();
            const preferredTitle = String(preferred.phone || card?.phone || "").trim();

            const titleMatches = preferredTitle
                ? options.filter(
                    (option) => String(option.raw?.title) === preferredTitle,
                )
                : [];

            const selected =
                options.find((option) =>
                    preferredSerialId && String(option.raw?.serialId) === preferredSerialId,
                ) ||
                options.find((option) =>
                    preferredImei && String(option.raw?.imei) === preferredImei,
                ) ||
                (titleMatches.length === 1 ? titleMatches[0] : null);

            if (preserveSelection) {
                // Al refrescar, no sobrescribir la elección del asesor ni completar un
                // IMEI por coincidencia de modelo. Solo sincronizar la opción seleccionada.
                setStepData((current) => {
                    const serialId = String(current.inventorySerialId || current.phoneValue?.raw?.serialId || "");
                    if (!serialId) return current;
                    const match = options.find((option) => String(option.raw?.serialId) === serialId);
                    return match ? { ...current, phoneValue: match } : current;
                });
                return options;
            }

            if (selected) {
                const unit = selected.raw || {};
                setStepData((current) => ({
                    ...current,
                    phoneValue: selected,
                    phone: unit.title || current.phone || "",
                    imei: unit.imei || current.imei || "",
                    phonePrice: toMoneyUI(unit.salePrice ?? current.phonePrice),
                    inventoryProductId: unit.productId || null,
                    inventorySerialId: unit.serialId || null,
                    inventoryLocationId: unit.locationId || null,
                }));
            } else {
                // Un registro legado puede tener solo el nombre del modelo.
                // No se selecciona un IMEI por aproximación si existen varias unidades.
                setStepData((current) => ({
                    ...current,
                    phoneValue: null,
                    imei: "",
                    inventoryProductId: null,
                    inventorySerialId: null,
                    inventoryLocationId: null,
                }));
            }

            return options;
        } catch (error) {
            if (requestSeq !== inventoryRequestSeqRef.current || activeInventoryCardRef.current !== cardId) return [];
            console.error("cards.inventory-products error:", error);
            setInventoryUnitOptions([]);
            setInventoryUnitsContext({
                sedeId: card?.fk_sede_id || null,
                sedeName: card?.sede_name || "",
            });
            setStepErrors((current) => ({
                ...current,
                phoneValue:
                    error?.response?.data?.error ||
                    "No se pudo consultar el inventario disponible de esta sede.",
            }));
            return [];
        } finally {
            if (requestSeq === inventoryRequestSeqRef.current) setInventoryUnitsLoading(false);
        }
    }

    function refreshInventoryOnSelectorOpen() {
        const card = stepModal.card;
        if (!stepModal.open || !card?.id) return;
        loadInventoryUnitsForCard(card, {}, true);
    }

    function selectInventoryUnit(option) {
        const unit = option?.raw || null;
        setStep({
            phoneValue: option || null,
            phone: unit?.title || "",
            imei: unit?.imei || "",
            phonePrice: unit ? toMoneyUI(unit.salePrice) : "",
            inventoryProductId: unit?.productId || null,
            inventorySerialId: unit?.serialId || null,
            inventoryLocationId: unit?.locationId || null,
        });
        setStepErrors((current) => ({
            ...current,
            phoneValue: undefined,
            imei: undefined,
            phonePrice: undefined,
        }));
    }

    function openStepModal(card, newState) {
        if (isSoldCard(card) && Number(newState) !== 3) return;

        const saleType =
            card?.category === 2 ? SALE_TYPE_OPTIONS[1] : SALE_TYPE_OPTIONS[0];

        const financeVal = getFinanceOptionByContext(
            card?.category,
            card?.segment || "1",
        );

        const phoneOpt = phoneSelectOptions.find(
            (x) => String(x.label) === String(card.phone || ""),
        );

        let phonesObj = null;
        try {
            phonesObj =
                card?.phone_numbers && typeof card.phone_numbers === "string"
                    ? JSON.parse(card.phone_numbers)
                    : card?.phone_numbers || null;
        } catch {
            phonesObj = null;
        }

        const metaObj = parseSaleMeta(card);
        const reasonOptions = buildReasonOptions(newState, financeVal);

        const detailReasonFromMeta = metaObj?.detail_reason
            ? reasonOptions.find(
                (o) => String(o.value) === String(metaObj.detail_reason),
            ) ||
            reasonOptions.find(
                (o) => String(o.label) === String(metaObj.detail_reason_label || ""),
            ) ||
            null
            : null;

        const notesFromMeta = metaObj?.notes != null ? String(metaObj.notes) : "";
        const initialFromMeta =
            metaObj?.initial_amount != null ? String(metaObj.initial_amount) : "";
        const futureFromMeta = metaObj?.future_buy_date
            ? String(metaObj.future_buy_date)
            : "";
        const contractEndFromMeta = metaObj?.contract_end_date
            ? String(metaObj.contract_end_date)
            : "";

        const countryIso2 = String(
            metaObj?.country_iso2 || PERU_ISO2,
        ).toLowerCase();
        const countryName = String(
            metaObj?.country_name ||
            getCountryOption(countryIso2)?.label ||
            PERU_COUNTRY_NAME,
        );

        setStepErrors({});
        setStepData({
            country_iso2: countryIso2,
            country_name: countryName,
            country_dial_code: String(
                metaObj?.country_dial_code || getCountryDialCode(countryIso2),
            ),
            dni: String(card?.dni || ""),
            name: toNameCase(card?.name || ""),
            paternalSur: toNameCase(card?.paternalsur || ""),
            maternalSur: toNameCase(card?.maternalsur || ""),
            email: String(card?.email || ""),
            phone_client: String(phonesObj?.client || card?.phone_number || ""),
            phone_family: String(phonesObj?.family || ""),
            phone_friend: String(phonesObj?.friend || ""),

            sale_type: saleType,
            finance: financeVal,
            phoneValue: card?.phone
                ? {
                    value: `existing-${card?.id || ""}`,
                    label: card?.imei
                        ? `${card.phone} · IMEI ${card.imei}`
                        : String(card.phone),
                    raw: {
                        title: String(card.phone),
                        imei: String(card?.imei || ""),
                        productId: Number(metaObj?.inventory_product_id || 0) || null,
                        serialId: Number(metaObj?.inventory_serial_id || 0) || null,
                        locationId: Number(metaObj?.inventory_location_id || 0) || null,
                        salePrice: Number(card?.phone_price || 0),
                    },
                }
                : phoneOpt || null,
            phone: card?.phone || phoneOpt?.label || "",
            inventoryProductId: Number(metaObj?.inventory_product_id || 0) || null,
            inventorySerialId: Number(metaObj?.inventory_serial_id || 0) || null,
            inventoryLocationId: Number(metaObj?.inventory_location_id || 0) || null,

            detail_reason:
                (newState === 0 || newState === 2) && Number(metaObj?.step || 0) >= 2
                    ? detailReasonFromMeta
                    : null,
            description:
                (newState === 0 || newState === 2) && Number(metaObj?.step || 0) >= 2
                    ? normalizeDescriptionUX(notesFromMeta)
                    : "",
            initial_amount:
                newState === 2 && String(metaObj?.detail_reason || "") === "NO_INITIAL"
                    ? String(initialFromMeta)
                    : "",
            future_buy_date:
                newState === 2 && String(metaObj?.detail_reason || "") === "BUY_LATER"
                    ? String(futureFromMeta)
                    : "",
            contract_end_date: newState === 3 ? String(contractEndFromMeta) : "",

            imei: card?.imei || "",
            phonePrice: toMoneyUI(card?.phone_price),
            fm: {
                value: String(card?.fm || "0"),
                label: String(card?.fm || "0") === "1" ? "Sí" : "No",
            },
            fmAmount: toMoneyUI(card?.fm_amount),
            cashAmount: toMoneyUI(card?.cash_amount),
            onlinePay:
                ONLINE_PAY_OPTIONS.find(
                    (x) => String(x.value) === String(card?.online_pay || "0"),
                ) || ONLINE_PAY_OPTIONS[0],
            onlineAmount: toMoneyUI(card?.online_amount),
            receiptType: RECEIPT_TYPE_OPTIONS.find((x) => x.value === metaObj?.receipt_type) || RECEIPT_TYPE_OPTIONS[0],
            customerRuc: metaObj?.customer_ruc || "",
            customerBusinessName: metaObj?.customer_business_name || "",
            customerAddress: metaObj?.customer_address || "",
            manager_id: card?.fk_manager_id
                ? managersOptions.find(
                    (m) => String(m.value) === String(card.fk_manager_id),
                )
                : null,
        });

        resetInitialValidationState();
        resetRucLookupState();
        setSaleReviewPopup({ open: false, context: null, step: 0 });
        setInventoryUnitOptions([]);
        setInventoryUnitsContext({
            sedeId: card?.fk_sede_id || null,
            sedeName: card?.sede_name || "",
        });
        activeInventoryCardRef.current = Number(card?.id || 0);
        ++inventoryRequestSeqRef.current;
        setStepModal({ open: true, card, state: newState });
        document.body.classList.add("no-scroll");

        if ([0, 2, 3].includes(Number(newState))) {
            loadInventoryUnitsForCard(card, {
                inventorySerialId: metaObj?.inventory_serial_id,
                imei: card?.imei,
                phone: card?.phone,
            });
        }

        if (Number(newState) === 3 && Number(card?.category) === 1) {
            setTimeout(() => {
                verifyInitialValidation();
            }, 0);
        }
    }

    function closeStepModal() {
        activeInventoryCardRef.current = null;
        ++inventoryRequestSeqRef.current;
        setInventoryUnitsLoading(false);
        resetInitialValidationState();
        resetRucLookupState();
        setSaleReviewPopup({ open: false, context: null, step: 0 });
        setInventoryUnitOptions([]);
        setInventoryUnitsContext({ sedeId: null, sedeName: "" });
        setStepModal({ open: false, card: null, state: null });
        document.body.classList.remove("no-scroll");
    }

    function validateStep(state, payload) {
        const err = {};

        if (state === 1) {
            if (!validateDocumentByCountry(payload.dni, payload.country_iso2))
                err.dni = getDocumentErrorMessage(payload.country_iso2);
            if (!String(payload.name || "").trim())
                err.name = "Nombres son obligatorios.";
            if (!String(payload.paternalSur || "").trim())
                err.paternalSur = "Apellido paterno es obligatorio.";
            if (!String(payload.maternalSur || "").trim())
                err.maternalSur = "Apellido materno es obligatorio.";
            const emailTrim = String(payload.email || "").trim();
            const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim);
            if (!emailTrim || !emailOk)
                err.email = "Email es obligatorio y debe ser válido.";
            if (isPeruCountry(payload.country_iso2)) {
                const mob = String(payload.phone_client || "")
                    .replace(/\D/g, "")
                    .slice(-9);
                if (!/^9\d{8}$/.test(mob))
                    err.phone_client = "Celular del cliente es obligatorio (9 dígitos).";
            }
            return err;
        }

        // El correo se solicita al gestionar cualquier estado de venta,
        // nunca durante la creación inicial del prospecto.
        if ([0, 2, 3].includes(Number(state))) {
            const email = String(payload.email || "").trim();
            if (!isEmail(email))
                err.email = "Email es obligatorio y debe ser válido.";
        }

        if (!payload.sale_type) err.sale_type = "Tipo es obligatorio.";
        if (!payload.finance) err.finance = "Fuente es obligatoria.";
        if (!payload.phoneValue) err.phoneValue = "Equipo / Modelo es obligatorio.";

        if (state === 0 || state === 2) {
            if (!payload.detail_reason) err.detail_reason = "Selecciona el motivo.";
            if (payload.detail_reason?.value === "NO_INITIAL") {
                const n = moneyToNumber(payload.initial_amount);
                if (!(n > 0)) err.initial_amount = "Ingresa la inicial (mayor a 0).";
            }
            if (payload.detail_reason?.value === "BUY_LATER") {
                if (!String(payload.future_buy_date || "").trim())
                    err.future_buy_date = "Selecciona la fecha estimada.";
            }
            if (payload.description && String(payload.description).length > 150)
                err.description = "Máx 150 caracteres.";
        }

        if (state === 3) {
            if (!String(payload.contract_end_date || "").trim()) {
                err.contract_end_date =
                    "Selecciona la fecha de finalización del contrato.";
            } else {
                const selectedContractEnd = moment
                    .tz(String(payload.contract_end_date), "YYYY-MM-DD", true, TZ)
                    .startOf("day");
                const minContractEnd = moment().tz(TZ).startOf("day");
                const maxContractEnd = minContractEnd.clone().add(1, "year");

                if (!selectedContractEnd.isValid()) {
                    err.contract_end_date = "Fecha de finalización inválida.";
                } else if (selectedContractEnd.isBefore(minContractEnd, "day")) {
                    err.contract_end_date = "La fecha no puede ser anterior a hoy.";
                } else if (selectedContractEnd.isAfter(maxContractEnd, "day")) {
                    err.contract_end_date =
                        "La fecha máxima permitida es 1 año después de hoy.";
                }
            }
            if (!payload.receiptType) err.receiptType = "Selecciona el comprobante.";
            if (payload.receiptType?.value === "FACTURA") {
                if (!/^\d{11}$/.test(String(payload.customerRuc || "").trim())) err.customerRuc = "RUC inválido (11 dígitos).";
                if (!String(payload.customerBusinessName || "").trim()) err.customerBusinessName = "Razón social obligatoria.";
                if (!String(payload.customerAddress || "").trim()) err.customerAddress = "Dirección fiscal obligatoria.";
            }
            if (!/^\d{15}$/.test(String(payload.imei || "").trim()))
                err.imei = "IMEI inválido (15 dígitos).";
            const phonePrice = moneyToNumber(payload.phonePrice);
            if (!(phonePrice > 0))
                err.phonePrice = "Precio del equipo es obligatorio.";

            const onlinePay = String(payload.onlinePay?.value || "0");
            const online =
                onlinePay === "0" ? 0 : moneyToNumber(payload.onlineAmount);
            if (onlinePay !== "0" && !(online > 0))
                err.onlineAmount = "Monto online es obligatorio.";

            if (String(payload.fm?.value || "0") === "1") {
                const fmA = moneyToNumber(payload.fmAmount);
                if (!(fmA > 0)) err.fmAmount = "Monto FM es obligatorio.";
            }
        }

        return err;
    }

    function buildSoldPayload() {
        const sale_type = stepData.sale_type?.value || "credit";
        const finance = sale_type === "cash" ? "4" : stepData.finance?.value || "1";
        const phoneLabel = stepData.phoneValue?.raw?.title || stepData.phone || "";
        const cash = moneyToNumber(stepData.cashAmount);
        const onlinePay = String(stepData.onlinePay?.value || "0");
        const online = onlinePay === "0" ? 0 : moneyToNumber(stepData.onlineAmount);

        return {
            sale_state: 3,
            sale_type,
            finance,
            phone: phoneLabel,
            manager_id: stepData.manager_id?.value || null,
            sale_meta: {
                state: 3,
                step: 2,
                detail_reason: null,
                detail_reason_label: null,
                initial_amount:
                    cash +
                    online +
                    (sale_type === "credit" && String(stepData.fm?.value || "0") === "1"
                        ? moneyToNumber(stepData.fmAmount)
                        : 0),
                future_buy_date: null,
                contract_end_date: stepData.contract_end_date || null,
                inventory_product_id: stepData.inventoryProductId || null,
                inventory_serial_id: stepData.inventorySerialId || null,
                inventory_location_id: stepData.inventoryLocationId || null,
                inventory_imei: String(stepData.imei || "").trim() || null,
                notes: null,
            },
            imei: String(stepData.imei || "").trim(),
            phonePrice: moneyToNumber(stepData.phonePrice),
            fm: String(stepData.fm?.value || "0"),
            fmAmount: moneyToNumber(stepData.fmAmount),
            cashAmount: cash,
            onlinePay,
            onlineAmount: online,
            receiptType: stepData.receiptType?.value,
            customerRuc: String(stepData.customerRuc || "").trim(),
            customerBusinessName: String(stepData.customerBusinessName || "").trim(),
            customerAddress: String(stepData.customerAddress || "").trim(),
        };
    }

    function shouldOpenFmPopupForSold() {
        const saleType = stepData.sale_type?.value || "credit";
        const finance = String(
            saleType === "cash" ? "4" : stepData.finance?.value || "1",
        );
        const wantsFM = String(stepData.fm?.value || "0") === "1";
        const fmAmountNum = parseMoneyPopup(stepData.fmAmount);
        return (
            saleType === "credit" &&
            wantsFM &&
            fmAmountNum > 0 &&
            ["1", "2", "4", "5"].includes(finance)
        );
    }

    function openFmFlowForSold() {
        const saleType = stepData.sale_type?.value || "credit";
        const finance = String(
            saleType === "cash" ? "4" : stepData.finance?.value || "1",
        );
        const currentCard = stepModal.card;

        setPendingFmContext({
            cardId: currentCard?.id || null,
            soldPayload: buildSoldPayload(),
            popupInitial: {
                category: saleType === "cash" ? "2" : "1",
                cat: saleType === "cash" ? "2" : "1",
                subCategory: "1",
                subCat: "1",
                segment: finance,
                amountFM: parseMoneyPopup(stepData.fmAmount),
                dni: String(stepData.dni || "").trim(),
                name: toNameCase(stepData.name || ""),
                paternalSur: toNameCase(stepData.paternalSur || ""),
                maternalSur: toNameCase(stepData.maternalSur || ""),
                email: String(stepData.email || "")
                    .trim()
                    .toLowerCase(),
                addressName: "",
                phoneNumber: String(stepData.phone_client || "").trim(),
                modelPhone: String(
                    stepData.phoneValue?.raw?.title || stepData.phone || "",
                ).trim(),
                imei: String(stepData.imei || "").trim(),
            },
            forcePayjoyMode: finance === "1",
            hideCuota: false,
        });

        setStepModal({ open: false, card: null, state: null });
        document.body.classList.remove("no-scroll");

        setTimeout(() => {
            setShowFmModal(true);
        }, 0);
    }

    async function downloadSaleCompletionDocument(result, receiptOverride = null) {
        const selected = receiptOverride || result;
        const receiptId = selected?.id || selected?.receiptId || result?.receiptId;
        if (!receiptId) return;
        const isBoleta = String(result.documentType || "") === "03";
        const endpoint = isBoleta
            ? `/inventory/sale-receipts/${receiptId}/ticket`
            : `/inventory/sale-receipts/${receiptId}/pdf`;
        const response = await api.get(endpoint, { responseType: "blob" });
        const url = URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        const selectedNumber = selected?.fullNumber || result?.fullNumber || "comprobante";
        const role = String(selected?.role || selected?.documentRole || "").toUpperCase();
        const suffix = role === "ADVANCE" ? "anticipo" : role === "FINAL" ? "boleta-final" : "boleta";
        a.download = isBoleta
            ? `${selectedNumber}-${suffix}.pdf`
            : `${selectedNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    }

    async function persistCardUpdate(id, payload, successMessage = "") {
        const res = await api.post(`/cards/${id}/update`, payload);
        if (res?.data?.msg === "success") {
            closeStepModal();
            await fetchCards();

            if (res?.data?.receiptId && res?.data?.saleCompleted) {
                setSaleCompletion(res.data);
            } else {
                const receiptMessage = res?.data?.fullNumber
                    ? `${successMessage || "Venta registrada correctamente."} Comprobante ${res.data.fullNumber} · SUNAT ${res.data.sunatStatus || "PROCESADO"}.`
                    : successMessage;
                if (receiptMessage) showSuccessToast(receiptMessage);
            }
            return res?.data || true;
        }
        alert(res?.data?.error || "No se pudo guardar.");
        return false;
    }

    function getSaleReviewFinanceLabel() {
        if (stepData.sale_type?.value === "cash") return FINANCE_OPTIONS_CASH[0].label;
        return stepData.finance?.label || "—";
    }

    function getSaleReviewStepKeys() {
        const isCash = stepData.sale_type?.value === "cash";
        const hasFm = !isCash && String(stepData.fm?.value || "0") === "1";

        if (isCash) {
            return ["sale_type", "equipment", "payment", "receipt"];
        }

        return [
            "sale_type",
            "finance",
            "equipment",
            "fm",
            ...(hasFm ? ["fm_amount"] : []),
            "payment",
            "receipt",
        ];
    }

    function closeSaleReviewPopup() {
        if (saleReviewPopup.context === "prospect") setPendingCreatePayload(null);
        setSaleReviewPopup({ open: false, context: null, step: 0 });
    }

    async function confirmSaleReviewPopup() {
        if (saleReviewPopup.context === "prospect") {
            const payload = pendingCreatePayload;
            closeSaleReviewPopup();
            if (payload) await persistCreatePayload(payload);
            return;
        }

        const steps = getSaleReviewStepKeys();
        const currentStep = Number(saleReviewPopup.step || 1);
        if (currentStep < steps.length) {
            setSaleReviewPopup({
                open: true,
                context: "sale",
                step: currentStep + 1,
            });
            return;
        }

        closeSaleReviewPopup();
        await onSaveStep2();
    }

    async function onSaveStep2() {
        if (saveStep2Ref.current) return;
        saveStep2Ref.current = true;
        setSavingStep2(true);

        const state = stepModal.state;
        // Para Vendido, la confirmación ya se realiza con el popup profesional
        // paso a paso. Conservamos el confirm legado para los demás estados.
        if (Number(state) !== 3) {
            const confirmText =
                "¿Desea Guardar la Información?\n\nLe recomendamos revisar la información para asegurar que sea la correcta.";
            if (!confirm(confirmText)) {
                saveStep2Ref.current = false;
                setSavingStep2(false);
                return;
            }
        }

        if (state === 1) {
            const err1 = validateStep(1, stepData);
            setStepErrors(err1);
            if (Object.keys(err1).length) {
                saveStep2Ref.current = false;
                setSavingStep2(false);
                return;
            }

            const currentMeta = parseSaleMeta(stepModal.card) || {};
            const payload1 = {
                sale_state: 1,
                country_iso2: String(stepData.country_iso2 || PERU_ISO2).toLowerCase(),
                country_name: String(stepData.country_name || PERU_COUNTRY_NAME).trim(),
                country_dial_code: String(
                    stepData.country_dial_code ||
                    getCountryDialCode(stepData.country_iso2),
                ).trim(),
                dni: String(stepData.dni || "").trim(),
                name: toNameCase(stepData.name),
                paternalSur: toNameCase(stepData.paternalSur),
                maternalSur: toNameCase(stepData.maternalSur),
                email: String(stepData.email || "")
                    .trim()
                    .toLowerCase(),
                phone_client: String(stepData.phone_client || "").trim(),
                phone_family: String(stepData.phone_family || "").trim(),
                phone_friend: String(stepData.phone_friend || "").trim(),
                sale_meta: {
                    ...currentMeta,
                    step: 1,
                    country_iso2: String(
                        stepData.country_iso2 || PERU_ISO2,
                    ).toLowerCase(),
                    country_name: String(
                        stepData.country_name || PERU_COUNTRY_NAME,
                    ).trim(),
                    country_dial_code: String(
                        stepData.country_dial_code ||
                        getCountryDialCode(stepData.country_iso2),
                    ).trim(),
                    edited_at: moment().tz(TZ).format(),
                },
            };

            setLoading(true);
            try {
                await persistCardUpdate(
                    stepModal.card?.id,
                    payload1,
                    getSuccessToastMessage(1),
                );
            } catch (e) {
                console.error(e);
                const detail = e?.response?.data;
            let retryHint = "";
            if (detail?.documentId) {
                if (detail.inventoryReservationStatus === "RELEASED") {
                    retryHint = `\n\nEl equipo volvió a estar DISPONIBLE en inventario. El comprobante ${detail.fullNumber || ""} se conserva para trazabilidad y podrá reintentarse con el mismo correlativo.`;
                } else if (["SUNAT_PROCESSING", "SUNAT_UNCERTAIN"].includes(detail.errorCategory)) {
                    retryHint = `\n\nEl IMEI permanece RESERVADO para evitar una doble venta. No generes otro comprobante. Reintenta ${detail.retryAfterAt ? `después de ${detail.retryAfterAt}` : "desde Inventario → Comprobantes"}.`;
                } else {
                    retryHint = `\n\nSe conserva el comprobante ${detail.fullNumber || ""}. Revisa Inventario → Comprobantes antes de intentar una nueva venta.`;
                }
            }
            alert((detail?.error || "Error guardando") + retryHint);
            } finally {
                setLoading(false);
                saveStep2Ref.current = false;
                setSavingStep2(false);
            }
            return;
        }

        const err = validateStep(state, {
            ...stepData,
            sale_type: stepData.sale_type,
            finance: stepData.finance,
            phoneValue: stepData.phoneValue,
            onlinePay: stepData.onlinePay,
        });
        setStepErrors(err);
        if (Object.keys(err).length) {
            saveStep2Ref.current = false;
            setSavingStep2(false);
            return;
        }

        if (state === 3 && isStepCreditSale() && !initialValidation.completed) {
            const checked = await verifyInitialValidation();
            if (!checked) {
                setStepErrors((prev) => ({
                    ...prev,
                    initial_validation:
                        "Debe completar y verificar la Validación de Inicial del Cliente antes de guardar.",
                }));
                saveStep2Ref.current = false;
                setSavingStep2(false);
                return;
            }
        }

        if (state === 3 && shouldOpenFmPopupForSold()) {
            saveStep2Ref.current = false;
            setSavingStep2(false);
            openFmFlowForSold();
            return;
        }

        const sale_type = stepData.sale_type?.value || "credit";
        const finance = sale_type === "cash" ? "4" : stepData.finance?.value || "1";
        const phoneLabel = stepData.phoneValue?.raw?.title || stepData.phone || "";
        const currentMeta = parseSaleMeta(stepModal.card) || {};

        const payload = {
            sale_state: state,
            sale_type,
            finance,
            phone: phoneLabel,
            manager_id: stepData.manager_id?.value || null,
            sale_meta: {
                ...currentMeta,
                state,
                step: 2,
                country_iso2: String(stepData.country_iso2 || PERU_ISO2).toLowerCase(),
                country_name: String(stepData.country_name || PERU_COUNTRY_NAME).trim(),
                country_dial_code: String(
                    stepData.country_dial_code ||
                    getCountryDialCode(stepData.country_iso2),
                ).trim(),
                detail_reason: stepData.detail_reason?.value || null,
                detail_reason_label: stepData.detail_reason?.label || null,
                initial_amount: moneyToNumber(stepData.initial_amount) || 0,
                future_buy_date: state === 3 ? null : stepData.future_buy_date || null,
                contract_end_date:
                    state === 3
                        ? stepData.contract_end_date || null
                        : currentMeta?.contract_end_date || null,
                inventory_product_id: stepData.inventoryProductId || null,
                inventory_serial_id: stepData.inventorySerialId || null,
                inventory_location_id: stepData.inventoryLocationId || null,
                inventory_imei: String(stepData.imei || "").trim() || null,
                notes: String(stepData.description || "").trim()
                    ? sanitizePlainText(stepData.description, 150)
                    : null,
            },
        };

        if (state === 3) {
            const cash = moneyToNumber(stepData.cashAmount);
            const onlinePay = String(stepData.onlinePay?.value || "0");
            const online =
                onlinePay === "0" ? 0 : moneyToNumber(stepData.onlineAmount);

            payload.email = String(stepData.email || "")
                .trim()
                .toLowerCase();

            payload.imei = String(stepData.imei || "").trim();
            payload.phonePrice = moneyToNumber(stepData.phonePrice);
            payload.fm = String(stepData.fm?.value || "0");
            payload.fmAmount = moneyToNumber(stepData.fmAmount);
            payload.cashAmount = cash;
            payload.onlinePay = onlinePay;
            payload.onlineAmount = online;
            payload.sale_meta.initial_amount =
                cash +
                online +
                (sale_type === "credit" && String(stepData.fm?.value || "0") === "1"
                    ? moneyToNumber(stepData.fmAmount)
                    : 0);
            payload.receiptType = stepData.receiptType?.value;
            payload.customerRuc = String(stepData.customerRuc || "").trim();
            payload.customerBusinessName = String(stepData.customerBusinessName || "").trim();
            payload.customerAddress = String(stepData.customerAddress || "").trim();
            payload.sale_meta.receipt_type = payload.receiptType;
            payload.sale_meta.customer_ruc = payload.customerRuc || null;
            payload.sale_meta.customer_business_name = payload.customerBusinessName || null;
            payload.sale_meta.customer_address = payload.customerAddress || null;
        }

        setLoading(true);
        try {
            if (Number(state) === 3) payload.promoGifts = selectedPromoGifts.map(({promotion_id,product_id,location_id,quantity}) => ({promotion_id,product_id,location_id,quantity}));
            await persistCardUpdate(
                stepModal.card?.id,
                payload,
                getSuccessToastMessage(state),
            );
        } catch (e) {
            console.error(e);
            const detail = e?.response?.data;
            const retryHint = detail?.documentId
                ? `\n\nSe creó el comprobante ${detail.fullNumber || ""} en estado pendiente/error. Puedes reenviarlo desde Inventario → Comprobantes sin generar otro correlativo.`
                : "";
            alert((detail?.error || "Error guardando") + retryHint);
        } finally {
            setLoading(false);
            saveStep2Ref.current = false;
            setSavingStep2(false);
        }
    }

    function onSaveStep2WithReview() {
        if (Number(stepModal.state) !== 3) {
            onSaveStep2();
            return;
        }

        // Antes de abrir la secuencia visual, valida el formulario actual para
        // no confirmar datos incompletos o inconsistentes.
        const err = validateStep(3, {
            ...stepData,
            sale_type: stepData.sale_type,
            finance: stepData.finance,
            phoneValue: stepData.phoneValue,
            onlinePay: stepData.onlinePay,
        });
        setStepErrors(err);
        if (Object.keys(err).length) return;

        setSaleReviewPopup({ open: true, context: "sale", step: 1 });
    }

    function canAssignManager(card) {
        return (
            Number(card?.sale_state) !== 0 &&
            Number(card?.sale_state) !== 3 &&
            Number(card?.category) === 1 &&
            Number(card?.segment) === 2
        );
    }

    function openManagerModal(card) {
        if (isSoldCard(card)) return;
        setSelectedManager(
            card?.fk_manager_id
                ? managersOptions.find(
                    (m) => String(m.value) === String(card.fk_manager_id),
                )
                : null,
        );
        setManagerModal({ open: true, card });
        document.body.classList.add("no-scroll");
    }

    function closeManagerModal() {
        setManagerModal({ open: false, card: null });
        document.body.classList.remove("no-scroll");
    }

    async function onSaveManager() {
        const confirmText =
            "¿Desea Guardar la Información?\n\nLe recomendamos revisar la información para asegurar que sea la correcta.";
        if (!confirm(confirmText)) return;

        if (!selectedManager) {
            alert("Selecciona un gestor.");
            return;
        }

        setLoading(true);
        try {
            const id = managerModal.card?.id;
            const res = await api.post(`/cards/${id}/update`, {
                manager_id: selectedManager.value,
            });
            if (res?.data?.msg === "success") {
                closeManagerModal();
                await fetchCards();
                showSuccessToast("Gestor asignado correctamente.");
            } else {
                alert(res?.data?.error || "No se pudo asignar gestor.");
            }
        } catch (e) {
            console.error(e);
            alert(e?.response?.data?.error || "Error asignando gestor");
        } finally {
            setLoading(false);
        }
    }

    const renderCreate = () => (
        <div ref={createCardRef} className="prospectCreateCard enterAnim">
            <div className="prospectCreateTop">
                <div className="prospectCreateTitle">Nuevo prospecto</div>
                <button className="cardsBtnGhost" onClick={() => setShowCreate(false)}>
                    Cerrar
                </button>
            </div>

            <div className="prospectGrid9">
                <div className="field fieldCountry">
                    <label>{requiredLabel("PAÍS")}</label>
                    <Select
                        options={countryOptions}
                        value={selectedCreateCountryOption}
                        onChange={(option) => applyCountryToCreate(option)}
                        styles={reactSelectStyles}
                        menuPortalTarget={
                            typeof document !== "undefined" ? document.body : null
                        }
                        placeholder="Selecciona un país"
                        isSearchable
                    />
                </div>

                <div className={`field ${createErrors.dni ? "fieldError" : ""}`}>
                    <label>
                        {requiredLabel(getDocumentLabel(createData.country_iso2))}
                    </label>
                    <div className="inputWithIcon">
                        <input
                            value={createData.dni}
                            onChange={(e) => {
                                const v = sanitizeDocumentInput(
                                    e.target.value,
                                    createData.country_iso2,
                                );
                                setCreate({ dni: v });
                                if (
                                    isPeruCountry(createData.country_iso2) &&
                                    String(v || "").trim().length === 8
                                ) {
                                    lookupDni(v, "create", createData.country_iso2);
                                }
                            }}
                            onBlur={() =>
                                lookupDni(createData.dni, "create", createData.country_iso2)
                            }
                            placeholder={getDocumentPlaceholder(createData.country_iso2)}
                        />
                        {isPeruCountry(createData.country_iso2) && dniLookupLoading ? (
                            <span className="miniSpinner" aria-label="Cargando" />
                        ) : null}
                    </div>
                    {createErrors.dni && (
                        <div className="fieldMsg">{createErrors.dni}</div>
                    )}
                </div>

                <div className={`field ${createErrors.name ? "fieldError" : ""}`}>
                    <label>{requiredLabel("Nombres")}</label>
                    <input
                        value={createData.name}
                        disabled={isPeruCountry(createData.country_iso2)}
                        onChange={(e) => setCreate({ name: toNameCase(e.target.value) })}
                        style={{ textTransform: "capitalize" }}
                    />
                    {createErrors.name && (
                        <div className="fieldMsg">{createErrors.name}</div>
                    )}
                </div>

                <div
                    className={`field ${createErrors.paternalSur ? "fieldError" : ""}`}
                >
                    <label>{requiredLabel("Apellido paterno")}</label>
                    <input
                        value={createData.paternalSur}
                        disabled={isPeruCountry(createData.country_iso2)}
                        onChange={(e) =>
                            setCreate({ paternalSur: toNameCase(e.target.value) })
                        }
                        style={{ textTransform: "capitalize" }}
                    />
                    {createErrors.paternalSur && (
                        <div className="fieldMsg">{createErrors.paternalSur}</div>
                    )}
                </div>

                <div
                    className={`field ${createErrors.maternalSur ? "fieldError" : ""}`}
                >
                    <label>{requiredLabel("Apellido materno")}</label>
                    <input
                        value={createData.maternalSur}
                        disabled={isPeruCountry(createData.country_iso2)}
                        onChange={(e) =>
                            setCreate({ maternalSur: toNameCase(e.target.value) })
                        }
                        style={{ textTransform: "capitalize" }}
                    />
                    {createErrors.maternalSur && (
                        <div className="fieldMsg">{createErrors.maternalSur}</div>
                    )}
                </div>

                <div
                    className={`field ${createErrors.phone_client ? "fieldError" : ""}`}
                >
                    <label>{requiredLabel("Celular del cliente")}</label>
                    <input
                        value={createData.phone_client}
                        onChange={(e) => {
                            handlePhoneInput(e);
                            setCreate({
                                phone_client: normalizePeruMobileUI(e.target.value),
                            });
                        }}
                        placeholder="9 dígitos"
                    />
                    {createErrors.phone_client && (
                        <div className="fieldMsg">{createErrors.phone_client}</div>
                    )}
                </div>

                <div
                    className={`field ${createErrors.phone_family ? "fieldError" : ""}`}
                >
                    <label>{optionalLabel("Celular 2")}</label>
                    <input
                        value={createData.phone_family}
                        onFocus={() => {
                            if (!String(createData.phone_family || "").trim())
                                setCreate({ phone_family: "9" });
                        }}
                        onChange={(e) => {
                            handlePhoneInput(e);
                            setCreate({
                                phone_family: normalizePeruMobileUI(e.target.value, {
                                    allowEmpty: true,
                                }),
                            });
                        }}
                        placeholder="9 dígitos"
                    />
                    {createErrors.phone_family && (
                        <div className="fieldMsg">{createErrors.phone_family}</div>
                    )}
                </div>

                <div
                    className={`field ${createErrors.phone_friend ? "fieldError" : ""}`}
                >
                    <label>{optionalLabel("Celular 3")}</label>
                    <input
                        value={createData.phone_friend}
                        onFocus={() => {
                            if (!String(createData.phone_friend || "").trim())
                                setCreate({ phone_friend: "9" });
                        }}
                        onChange={(e) => {
                            handlePhoneInput(e);
                            setCreate({
                                phone_friend: normalizePeruMobileUI(e.target.value, {
                                    allowEmpty: true,
                                }),
                            });
                        }}
                        placeholder="9 dígitos"
                    />
                    {createErrors.phone_friend && (
                        <div className="fieldMsg">{createErrors.phone_friend}</div>
                    )}
                </div>
            </div>

            <div className="prospectCreateActions">
                <button className="cardsBtnGhost" onClick={() => setShowCreate(false)}>
                    Cancelar
                </button>
                <button className="cardsBtnPrimary" onClick={onCreateSave}>
                    Guardar prospecto
                </button>
            </div>
        </div>
    );

    const renderCard = (c) => {
        // console.log(c);
        const isExpanded = !!expanded[c.id];
        const isSoldLocked = isSoldCard(c);
        const stateMeta =
            SALE_STATES.find((s) => Number(s.value) === Number(c.sale_state)) ||
            SALE_STATES[1];

        const metaObj = parseSaleMeta(c) || {};
        const countryName = getCardCountryName(c);
        const reasonLabel = getReasonLabelFromMeta(c, metaObj);
        const showTypeFinance =
            Number(c.sale_state) >= 2 || Number(metaObj?.step || 0) >= 2;

        const tipoLabel = Number(c.category) === 2 ? "Contado" : "Crédito";
        const fuenteLabel = getFinanceLabelByContext(c.category, c.segment);

        const fullName =
            `${toNameCase(c.name || "")} ${toNameCase(c.paternalsur || "")} ${toNameCase(c.maternalsur || "")}`
                .replace(/\s+/g, " ")
                .trim();
        const cardDateTime = formatCardDateTime(c.creation_date);

        return (
            <div
                id={`card-${c.id}`}
                key={c.id}
                className={`prospectCard stateCard-${stateMeta.kind} ${isExpanded ? "isExpanded" : ""} ${isSoldLocked ? "cardLocked cardLockedFinal" : ""} enterAnim`}
            >
                <div className="prospectMain">
                    <div className="prospectHeadRow">
                        <div className="prospectHeadInfo">
                            {countryName ? <div className="prospectCountryBadge">País: {countryName}</div> : null}
                            {cardDateTime ? (
                                <div className="prospectDateTime">{cardDateTime}</div>
                            ) : null}
                            <div className="prospectName">
                                {fullName || "Sin nombre"}
                            </div>
                        </div>

                        <div className="prospectRightMeta">
                            {isSoldLocked && c.receipt_id && [1,3,5,7,8].includes(userRole) && (
                        <button className="cardsReceiptPdfButton" type="button" onClick={async()=>{
                            try { const response=await api.get(`/inventory/sale-receipts/${c.receipt_id}/pdf`,{responseType:"blob"}); const url=URL.createObjectURL(response.data); const a=document.createElement("a"); a.href=url; a.download=`${c.receipt_number || "comprobante"}.pdf`; a.click(); URL.revokeObjectURL(url); } catch(e){ alert(e?.response?.data?.error || "No se pudo descargar el comprobante."); }
                        }} title="Descargar comprobante PDF">📄 {c.receipt_number || "Comprobante"}</button>
                    )}

                    {canAssignManager(c) && (
                                <span className="managerChip" title="Gestor asignado">
                                    {c.manager_name ? `Gestor: ${c.manager_name}` : "Gestor: —"}
                                </span>
                            )}

                            <button
                                className="toggleBtn"
                                onClick={() =>
                                    setExpanded((prev) => ({ ...prev, [c.id]: !prev[c.id] }))
                                }
                                title={isExpanded ? "Ocultar detalles" : "Ver detalles"}
                            >
                                {isExpanded ? "Ocultar" : "Detalles"}
                            </button>
                        </div>
                    </div>

                    <div className="prospectPreviewRow">
                        <div className="previewItem">
                            <span className="k">Asesor:</span>{" "}
                            <span className="v">{c.advisor_name || "—"}</span>
                        </div>

                        <div className="previewItem">
                            <span className="k">Sede:</span>{" "}
                            <span className="v">{c.sede_name || "—"}</span>
                        </div>

                        <div className="previewItem">
                            <span className="k">Cel:</span>{" "}
                            <span className="v">{c.phone_number || "—"}</span>
                        </div>

                        {showTypeFinance ? (
                            <>
                                <div className="previewItem">
                                    <span className="k">Tipo:</span>{" "}
                                    <span className="v">{tipoLabel}</span>
                                </div>
                                <div className="previewItem">
                                    <span className="k">Fuente:</span>{" "}
                                    <span className="v">{fuenteLabel}</span>
                                </div>
                            </>
                        ) : (
                            <div className="previewItem muted">
                                <span className="k">Siguiente paso:</span>{" "}
                                <span className="v">Completa Tipo y Fuente</span>
                            </div>
                        )}
                        <div className={`statePill state-${stateMeta.kind}`}>
                            {stateMeta.label}
                        </div>
                    </div>

                    {isExpanded && (
                        <div className="prospectDetails expandAnim">
                            <div className="detailGrid">
                                {renderDetail("DNI", c.dni)}
                                {renderDetail("Email", c.email)}
                                {renderDetail("Teléfonos", c.phone_numbers_text)}
                                {renderDetail("Equipo / Modelo", c.phone)}
                                {renderDetail("Precio", c.phone_price, (v) =>
                                    Number(v).toFixed(2),
                                )}
                                {renderDetail("IMEI", c.imei)}
                                {renderDetail("Inicial Efec.", c.cash_amount, (v) =>
                                    Number(v).toFixed(2),
                                )}
                                {renderDetail("Inicial Online", c.online_amount, (v) =>
                                    Number(v).toFixed(2),
                                )}
                                {renderDetail("FM", c.fm_amount, (v) => Number(v).toFixed(2))}
                            </div>

                            {(metaObj || c.sale_meta) && (
                                <div className="metaBox">
                                    <div className="metaBoxHead">
                                        <div>
                                            <div className="metaEyebrow">- Seguimiento</div>
                                            <div className="metaTitle">Detalle comercial</div>
                                        </div>

                                        {reasonLabel ? (
                                            <span className="metaReasonBadge">{reasonLabel}</span>
                                        ) : null}
                                    </div>

                                    {metaObj ? (
                                        <div className="metaBody">
                                            <div className="metaInfoGrid">
                                                {reasonLabel ? (
                                                    <div className="metaCard">
                                                        <div className="metaCardK">Motivo</div>
                                                        <div className="metaCardV">{reasonLabel}</div>
                                                    </div>
                                                ) : null}

                                                {metaObj?.initial_amount != null &&
                                                    String(metaObj.initial_amount) !== "" &&
                                                    Number(metaObj.initial_amount || 0) > 0 && (
                                                        <div className="metaCard">
                                                            <div className="metaCardK">
                                                                Inicial disponible
                                                            </div>
                                                            <div className="metaCardV">
                                                                S/{" "}
                                                                {Number(metaObj.initial_amount || 0).toFixed(2)}
                                                            </div>
                                                        </div>
                                                    )}

                                                {metaObj?.future_buy_date && (
                                                    <div className="metaCard">
                                                        <div className="metaCardK">Fecha estimada</div>
                                                        <div className="metaCardV">
                                                            {formatMetaDate(metaObj.future_buy_date)}
                                                        </div>
                                                    </div>
                                                )}

                                                {metaObj?.contract_end_date && (
                                                    <div className="metaCard">
                                                        <div className="metaCardK">Fin de contrato</div>
                                                        <div className="metaCardV">
                                                            {formatMetaDate(metaObj.contract_end_date)}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {metaObj?.notes ? (
                                                <div className="metaNotesBox">
                                                    <div className="metaNotesK">Observación</div>
                                                    <div className="metaNotesV">
                                                        {String(metaObj.notes)}
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <pre className="metaPre">{String(c.sale_meta)}</pre>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="prospectFooter">
                    <div className="pillGroup">
                        {isSoldLocked ? (
                            <>
                                <button
                                    type="button"
                                    className="pillBtn pill-info isDisabled"
                                    disabled
                                    title="Venta pendiente"
                                >
                                    Venta pendiente
                                </button>
                                <button
                                    type="button"
                                    className="pillBtn pill-info isDisabled"
                                    disabled
                                    title="Evaluación aprobada"
                                >
                                    Evaluación aprobada
                                </button>
                                <button
                                    type="button"
                                    className="pillBtn active pill-success soldStaticPill"
                                    title="Vendido"
                                >
                                    Vendido
                                </button>
                                <button
                                    type="button"
                                    className="pillBtn pill-info isDisabled"
                                    disabled
                                    title="Rechazado"
                                >
                                    Rechazado
                                </button>
                            </>
                        ) : (
                            SALE_STATES.map((s) => (
                                <button
                                    key={s.value}
                                    className={`pillBtn ${Number(c.sale_state) === Number(s.value) ? "active" : ""} pill-${s.kind}`}
                                    onClick={() => openStepModal(c, s.value)}
                                    title={s.label}
                                >
                                    {s.label}
                                </button>
                            ))
                        )}
                    </div>

                    {isSoldLocked && c.receipt_id && [1,3,5,7,8].includes(userRole) && (
                        <button className="cardsReceiptPdfButton" type="button" onClick={async()=>{
                            try { const response=await api.get(`/inventory/sale-receipts/${c.receipt_id}/pdf`,{responseType:"blob"}); const url=URL.createObjectURL(response.data); const a=document.createElement("a"); a.href=url; a.download=`${c.receipt_number || "comprobante"}.pdf`; a.click(); URL.revokeObjectURL(url); } catch(e){ alert(e?.response?.data?.error || "No se pudo descargar el comprobante."); }
                        }} title="Descargar comprobante PDF">📄 {c.receipt_number || "Comprobante"}</button>
                    )}

                    {canAssignManager(c) && (
                        <button
                            className="cardsBtnPrimary small"
                            onClick={() => openManagerModal(c)}
                        >
                            Asignar gestor
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const renderStepModal = () => {
        if (!stepModal.open) return null;

        const state = stepModal.state;
        const title =
            SALE_STATES.find((s) => s.value === state)?.label || "Registro";
        const reasonOptions = buildReasonOptions(state, stepData.finance);

        const isCash = stepData.sale_type?.value === "cash";
        const financeOptions = isCash
            ? FINANCE_OPTIONS_CASH
            : FINANCE_OPTIONS_CREDIT;
        const financeValue = isCash ? FINANCE_OPTIONS_CASH[0] : stepData.finance;

        const showInitial =
            state === 2 && stepData.detail_reason?.value === "NO_INITIAL";
        const showFutureDate =
            state === 2 && stepData.detail_reason?.value === "BUY_LATER";

        const noVendidoDetailLabel =
            state === 0 && stepData.detail_reason?.value === "NOT_APPROVED"
                ? `Cliente no aprobó en ${stepData.finance?.label || "la fuente"}`
                : null;

        const requiresInitialValidation = state === 3 && isStepCreditSale();
        const initialValidationCompleted = initialValidation.completed === true;

        return (
            <div className="modalOverlay" onMouseDown={closeStepModal}>
                <div
                    className="modalCard popAnim"
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <div className="modalTop">
                        <div className="modalTitle">
                            {title} {noVendidoDetailLabel ? `— ${noVendidoDetailLabel}` : ""}
                        </div>
                        <button className="cardsBtnGhost" onClick={closeStepModal}>
                            Cerrar
                        </button>
                    </div>

                    <div className="modalBody">
                        {state === 1 && (
                            <>
                                <div className="modalHint">
                                    Edita los datos del prospecto. (Este estado es el inicial.)
                                </div>

                                <div className="modalGrid4">
                                    <div className="field">
                                        <label>{requiredLabel("País")}</label>
                                        <Select
                                            options={countryOptions}
                                            value={selectedStepCountryOption}
                                            onChange={(option) => applyCountryToStep(option)}
                                            styles={reactSelectStyles}
                                            menuPortalTarget={
                                                typeof document !== "undefined" ? document.body : null
                                            }
                                            placeholder="Selecciona un país"
                                            isSearchable
                                        />
                                    </div>

                                    <div
                                        className={`field ${stepErrors.dni ? "fieldError" : ""}`}
                                    >
                                        <label>
                                            {requiredLabel(getDocumentLabel(stepData.country_iso2))}
                                        </label>
                                        <div className="inputWithIcon">
                                            <input
                                                value={stepData.dni}
                                                onChange={(e) => {
                                                    const v = sanitizeDocumentInput(
                                                        e.target.value,
                                                        stepData.country_iso2,
                                                    );
                                                    setStep({ dni: v });
                                                    if (
                                                        isPeruCountry(stepData.country_iso2) &&
                                                        String(v || "").trim().length === 8
                                                    ) {
                                                        lookupDni(v, "step", stepData.country_iso2);
                                                    }
                                                }}
                                                onBlur={() =>
                                                    lookupDni(stepData.dni, "step", stepData.country_iso2)
                                                }
                                                placeholder={getDocumentPlaceholder(
                                                    stepData.country_iso2,
                                                )}
                                            />
                                            {isPeruCountry(stepData.country_iso2) &&
                                                dniLookupLoading ? (
                                                <span className="miniSpinner" aria-label="Cargando" />
                                            ) : null}
                                        </div>
                                        {stepErrors.dni && (
                                            <div className="fieldMsg">{stepErrors.dni}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field ${stepErrors.name ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Nombres")}</label>
                                        <input
                                            value={stepData.name}
                                            disabled={isPeruCountry(stepData.country_iso2)}
                                            onChange={(e) =>
                                                setStep({ name: toNameCase(e.target.value) })
                                            }
                                            style={{ textTransform: "capitalize" }}
                                        />
                                        {stepErrors.name && (
                                            <div className="fieldMsg">{stepErrors.name}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field ${stepErrors.paternalSur ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Apellido paterno")}</label>
                                        <input
                                            value={stepData.paternalSur}
                                            disabled={isPeruCountry(stepData.country_iso2)}
                                            onChange={(e) =>
                                                setStep({ paternalSur: toNameCase(e.target.value) })
                                            }
                                            style={{ textTransform: "capitalize" }}
                                        />
                                        {stepErrors.paternalSur && (
                                            <div className="fieldMsg">{stepErrors.paternalSur}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field ${stepErrors.maternalSur ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Apellido materno")}</label>
                                        <input
                                            value={stepData.maternalSur}
                                            disabled={isPeruCountry(stepData.country_iso2)}
                                            onChange={(e) =>
                                                setStep({ maternalSur: toNameCase(e.target.value) })
                                            }
                                            style={{ textTransform: "capitalize" }}
                                        />
                                        {stepErrors.maternalSur && (
                                            <div className="fieldMsg">{stepErrors.maternalSur}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field fieldSpan2 ${stepErrors.email ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Email")}</label>
                                        <input
                                            value={stepData.email}
                                            onChange={(e) => {
                                                handleEmailInput(e);
                                                setStep({ email: e.target.value });
                                            }}
                                            placeholder="correo@dominio.com"
                                        />
                                        {stepErrors.email && (
                                            <div className="fieldMsg">{stepErrors.email}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field ${stepErrors.phone_client ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Celular del cliente")}</label>
                                        <input
                                            value={stepData.phone_client}
                                            onChange={(e) => {
                                                handlePhoneInput(e);
                                                setStep({
                                                    phone_client: normalizePeruMobileUI(e.target.value),
                                                });
                                            }}
                                            placeholder="9 dígitos"
                                        />
                                        {stepErrors.phone_client && (
                                            <div className="fieldMsg">{stepErrors.phone_client}</div>
                                        )}
                                    </div>

                                    <div className="field">
                                        <label>{optionalLabel("Celular 2")}</label>
                                        <input
                                            value={stepData.phone_family}
                                            onChange={(e) => {
                                                handlePhoneInput(e);
                                                setStep({
                                                    phone_family: normalizePeruMobileUI(e.target.value, {
                                                        allowEmpty: true,
                                                    }),
                                                });
                                            }}
                                            placeholder="(Opcional)"
                                        />
                                    </div>

                                    <div className="field">
                                        <label>{optionalLabel("Celular 3")}</label>
                                        <input
                                            value={stepData.phone_friend}
                                            onChange={(e) => {
                                                handlePhoneInput(e);
                                                setStep({
                                                    phone_friend: normalizePeruMobileUI(e.target.value, {
                                                        allowEmpty: true,
                                                    }),
                                                });
                                            }}
                                            placeholder="(Opcional)"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {(state === 0 || state === 2) && (
                            <>
                                <div className="modalGrid4">
                                    <div
                                        className={`field ${stepErrors.sale_type ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Tipo")}</label>
                                        <Select
                                            styles={reactSelectStyles}
                                            menuPortalTarget={document.body}
                                            options={SALE_TYPE_OPTIONS}
                                            value={stepData.sale_type}
                                            onChange={(v) => {
                                                const next = {
                                                    sale_type: v,
                                                    finance:
                                                        v?.value === "cash"
                                                            ? FINANCE_OPTIONS_CASH[0]
                                                            : FINANCE_OPTIONS_CREDIT[0],
                                                    detail_reason: null,
                                                    description: "",
                                                    initial_amount: "",
                                                    future_buy_date: "",
                                                };

                                                // El precio editable de Contado nunca debe arrastrarse a Crédito.
                                                // Al volver a Crédito restauramos el precio maestro del IMEI
                                                // seleccionado, obtenido desde Inventario.
                                                if (v?.value === "credit") {
                                                    const selectedUnit = stepData.phoneValue?.raw || null;
                                                    if (selectedUnit && Number.isFinite(Number(selectedUnit.salePrice))) {
                                                        next.phonePrice = toMoneyUI(selectedUnit.salePrice);
                                                    }
                                                }

                                                setStep(next);
                                            }}
                                        />
                                        {stepErrors.sale_type && (
                                            <div className="fieldMsg">{stepErrors.sale_type}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field ${stepErrors.finance ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Fuente")}</label>
                                        <Select
                                            styles={reactSelectStyles}
                                            menuPortalTarget={document.body}
                                            options={financeOptions}
                                            value={financeValue}
                                            isDisabled={isCash}
                                            onChange={(v) =>
                                                setStep({
                                                    finance: v,
                                                    detail_reason: null,
                                                    description: "",
                                                    initial_amount: "",
                                                    future_buy_date: "",
                                                })
                                            }
                                        />
                                        {stepErrors.finance && (
                                            <div className="fieldMsg">{stepErrors.finance}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field fieldSpan2 ${stepErrors.phoneValue ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Equipo / Modelo")}</label>
                                        <Select
                                            styles={reactSelectStyles}
                                            menuPortalTarget={document.body}
                                            options={inventoryUnitOptions}
                                            value={stepData.phoneValue}
                                            onChange={selectInventoryUnit}
                                            onMenuOpen={refreshInventoryOnSelectorOpen}
                                            placeholder={
                                                inventoryUnitsLoading
                                                    ? "Consultando inventario de la sede..."
                                                    : "Seleccionar producto e IMEI..."
                                            }
                                            isLoading={inventoryUnitsLoading}
                                            isSearchable
                                            noOptionsMessage={() =>
                                                inventoryUnitsLoading
                                                    ? "Consultando inventario..."
                                                    : "No hay equipos disponibles en la sede del asesor"
                                            }
                                        />
                                        <small className="cardsInventorySedeHint">
                                            {inventoryUnitsContext.sedeName
                                                ? `Inventario disponible: ${inventoryUnitsContext.sedeName}`
                                                : "Se consulta el inventario de la sede asignada al asesor."}
                                        </small>
                                        {stepData.phoneValue?.raw?.igvAffectationCode && (
                                            <small className="cardsInventorySedeHint">
                                                Afectación IGV del producto: {
                                                    IGV_AFFECTATION_LABELS[
                                                        String(stepData.phoneValue.raw.igvAffectationCode)
                                                    ] || "Código tributario configurado"
                                                } ({String(stepData.phoneValue.raw.igvAffectationCode)})
                                            </small>
                                        )}
                                        {stepErrors.phoneValue && (
                                            <div className="fieldMsg">{stepErrors.phoneValue}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field fieldSpan2 ${stepErrors.detail_reason ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Detalle")}</label>
                                        <Select
                                            styles={reactSelectStyles}
                                            menuPortalTarget={document.body}
                                            options={reasonOptions}
                                            value={stepData.detail_reason}
                                            onChange={(v) =>
                                                setStep({
                                                    detail_reason: v,
                                                    initial_amount: "",
                                                    future_buy_date: "",
                                                })
                                            }
                                            placeholder="Seleccionar..."
                                        />
                                        {stepErrors.detail_reason && (
                                            <div className="fieldMsg">{stepErrors.detail_reason}</div>
                                        )}
                                    </div>

                                    {showInitial && (
                                        <div
                                            className={`field ${stepErrors.initial_amount ? "fieldError" : ""}`}
                                        >
                                            <label>{requiredLabel("Inicial del cliente")}</label>
                                            <input
                                                placeholder="0.00"
                                                value={stepData.initial_amount}
                                                onChange={(e) => {
                                                    handlePriceInput(e);
                                                    setStep({ initial_amount: e.target.value });
                                                }}
                                            />
                                            {stepErrors.initial_amount && (
                                                <div className="fieldMsg">
                                                    {stepErrors.initial_amount}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {showFutureDate && (
                                        <div
                                            className={`field ${stepErrors.future_buy_date ? "fieldError" : ""}`}
                                        >
                                            <label>{requiredLabel("Fecha estimada de compra")}</label>
                                            <input
                                                ref={futureDateRef}
                                                value={stepData.future_buy_date}
                                                readOnly
                                                placeholder="Seleccionar fecha"
                                            />
                                            {stepErrors.future_buy_date && (
                                                <div className="fieldMsg">
                                                    {stepErrors.future_buy_date}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div
                                    className={`field ${stepErrors.description ? "fieldError" : ""}`}
                                >
                                    <label>{optionalLabel("Descripción")}</label>
                                    <textarea
                                        rows={3}
                                        value={stepData.description}
                                        onChange={(e) => {
                                            const normalized = normalizeDescriptionUX(e.target.value);
                                            handleCustomInput({ target: { value: normalized } }, 150);
                                            setStep({ description: normalized });
                                        }}
                                        placeholder="(Opcional) Observaciones..."
                                    />
                                    {stepErrors.description && (
                                        <div className="fieldMsg">{stepErrors.description}</div>
                                    )}
                                </div>
                            </>
                        )}

                        {state === 3 && (
                            <>
                                <div className="modalGrid4">
                                    <div
                                        className={`field ${stepErrors.sale_type ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Tipo")}</label>
                                        <Select
                                            styles={reactSelectStyles}
                                            menuPortalTarget={document.body}
                                            options={SALE_TYPE_OPTIONS}
                                            value={stepData.sale_type}
                                            onChange={(v) => {
                                                const isCashNow = v?.value === "cash";
                                                const next = {
                                                    sale_type: v,
                                                    finance: isCashNow
                                                        ? FINANCE_OPTIONS_CASH[0]
                                                        : FINANCE_OPTIONS_CREDIT[0],
                                                    fm: { value: "0", label: "No" },
                                                    fmAmount: "",
                                                };

                                                // Si el asesor aplicó una rebaja manual en Contado y luego
                                                // cambia la operación a Crédito, se descarta esa rebaja y
                                                // se restaura el precio vigente recibido desde Inventario.
                                                if (!isCashNow) {
                                                    const selectedUnit = stepData.phoneValue?.raw || null;
                                                    if (selectedUnit && Number.isFinite(Number(selectedUnit.salePrice))) {
                                                        next.phonePrice = toMoneyUI(selectedUnit.salePrice);
                                                    }
                                                }

                                                setStep(next);
                                            }}
                                        />
                                        {stepErrors.sale_type && (
                                            <div className="fieldMsg">{stepErrors.sale_type}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field ${stepErrors.finance ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Fuente")}</label>
                                        <Select
                                            styles={reactSelectStyles}
                                            menuPortalTarget={document.body}
                                            options={financeOptions}
                                            value={financeValue}
                                            isDisabled={isCash}
                                            onChange={(v) => {
                                                const nextIsCash = stepData.sale_type?.value === "cash";
                                                setStep({
                                                    finance: v,
                                                    fm:
                                                        nextIsCash || ["3"].includes(String(v?.value || ""))
                                                            ? { value: "0", label: "No" }
                                                            : stepData.fm,
                                                    fmAmount:
                                                        nextIsCash || ["3"].includes(String(v?.value || ""))
                                                            ? ""
                                                            : stepData.fmAmount,
                                                });
                                            }}
                                        />
                                        {stepErrors.finance && (
                                            <div className="fieldMsg">{stepErrors.finance}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field fieldSpan2 ${stepErrors.phoneValue ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Equipo / Modelo")}</label>
                                        <Select
                                            styles={reactSelectStyles}
                                            menuPortalTarget={document.body}
                                            options={inventoryUnitOptions}
                                            value={stepData.phoneValue}
                                            onChange={selectInventoryUnit}
                                            onMenuOpen={refreshInventoryOnSelectorOpen}
                                            placeholder={
                                                inventoryUnitsLoading
                                                    ? "Consultando inventario de la sede..."
                                                    : "Seleccionar producto e IMEI..."
                                            }
                                            isLoading={inventoryUnitsLoading}
                                            isSearchable
                                            noOptionsMessage={() =>
                                                inventoryUnitsLoading
                                                    ? "Consultando inventario..."
                                                    : "No hay equipos disponibles en la sede del asesor"
                                            }
                                        />
                                        <small className="cardsInventorySedeHint">
                                            {inventoryUnitsContext.sedeName
                                                ? `Inventario disponible: ${inventoryUnitsContext.sedeName}`
                                                : "Se consulta el inventario de la sede asignada al asesor."}
                                        </small>
                                        {stepData.phoneValue?.raw?.igvAffectationCode && (
                                            <small className="cardsInventorySedeHint">
                                                Afectación IGV del producto: {
                                                    IGV_AFFECTATION_LABELS[
                                                        String(stepData.phoneValue.raw.igvAffectationCode)
                                                    ] || "Código tributario configurado"
                                                } ({String(stepData.phoneValue.raw.igvAffectationCode)})
                                            </small>
                                        )}
                                        {stepErrors.phoneValue && (
                                            <div className="fieldMsg">{stepErrors.phoneValue}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field ${stepErrors.imei ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("IMEI")}</label>
                                        <input
                                            value={stepData.imei}
                                            disabled
                                            aria-disabled="true"
                                            placeholder="Se completa al seleccionar el equipo"
                                            title="El IMEI proviene del inventario de la sede y no se edita manualmente."
                                        />
                                        {stepErrors.imei && (
                                            <div className="fieldMsg">{stepErrors.imei}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field ${stepErrors.phonePrice ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Precio del equipo")}</label>
                                        <input
                                            placeholder="0.00"
                                            value={stepData.phonePrice}
                                            disabled={stepData.sale_type?.value === "credit"}
                                            aria-disabled={stepData.sale_type?.value === "credit"}
                                            title={
                                                stepData.sale_type?.value === "credit"
                                                    ? "En ventas a crédito el precio proviene del inventario y no se puede modificar."
                                                    : "En ventas al contado el precio puede modificarse antes de guardar."
                                            }
                                            onChange={(e) => {
                                                handlePriceInput(e);
                                                setStep({ phonePrice: e.target.value });
                                            }}
                                        />
                                        {stepErrors.phonePrice && (
                                            <div className="fieldMsg">{stepErrors.phonePrice}</div>
                                        )}
                                        {stepData.sale_type?.value === "cash" && (() => {
                                            const rawOfficialPrice = stepData.phoneValue?.raw?.salePrice;
                                            const hasOfficialPrice = rawOfficialPrice !== null && rawOfficialPrice !== undefined && rawOfficialPrice !== "" && Number.isFinite(Number(rawOfficialPrice));
                                            const enteredPrice = moneyToNumber(stepData.phonePrice);
                                            const hasEnteredPrice = String(stepData.phonePrice ?? "").trim() !== "" && enteredPrice > 0;
                                            const officialCents = hasOfficialPrice ? Math.round(Number(rawOfficialPrice) * 100) : 0;
                                            const enteredCents = Math.round(enteredPrice * 100);
                                            const differenceCents = enteredCents - officialCents;
                                            const status = differenceCents < 0 ? "discount" : differenceCents > 0 ? "increase" : "equal";
                                            return (
                                                <div className={`cards-cash-difference ${hasOfficialPrice && hasEnteredPrice ? `is-${status}` : "is-pending"}`} aria-live="polite">
                                                    <div className="cards-cash-difference__heading">
                                                        <span>{!hasOfficialPrice || !hasEnteredPrice ? "Diferencia de precio" : status === "discount" ? "Total descuento" : status === "increase" ? "Incremento de precio" : "Sin diferencia de precio"}</span>
                                                        <strong>{hasOfficialPrice && hasEnteredPrice ? `S/ ${(Math.abs(differenceCents) / 100).toFixed(2)}` : "—"}</strong>
                                                    </div>
                                                    <small>{hasOfficialPrice ? `Precio de Inventario: S/ ${(officialCents / 100).toFixed(2)}` : "Selecciona un equipo con precio de Inventario."}</small>
                                                    {!hasEnteredPrice && hasOfficialPrice && <small>Ingresa el precio de venta para calcular la diferencia.</small>}
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    <div className="field">
                                        <label>FM</label>
                                        <Select
                                            styles={reactSelectStyles}
                                            menuPortalTarget={document.body}
                                            options={[
                                                { value: "0", label: "No" },
                                                { value: "1", label: "Sí" },
                                            ]}
                                            value={
                                                stepData.sale_type?.value === "cash" ||
                                                    ["3"].includes(String(stepData.finance?.value || ""))
                                                    ? { value: "0", label: "No" }
                                                    : stepData.fm
                                            }
                                            isDisabled={
                                                stepData.sale_type?.value === "cash" ||
                                                ["3"].includes(String(stepData.finance?.value || ""))
                                            }
                                            onChange={(v) => setStep({ fm: v, fmAmount: "" })}
                                        />
                                    </div>

                                    <div
                                        className={`field ${stepErrors.fmAmount ? "fieldError" : ""}`}
                                    >
                                        <label>Monto FM</label>
                                        <input
                                            placeholder="0.00"
                                            value={stepData.fmAmount}
                                            disabled={
                                                String(stepData.fm?.value || "0") !== "1" ||
                                                stepData.sale_type?.value === "cash" ||
                                                ["3"].includes(String(stepData.finance?.value || ""))
                                            }
                                            onChange={(e) => {
                                                handlePriceInput(e);
                                                setStep({ fmAmount: e.target.value });
                                            }}
                                        />
                                        {stepErrors.fmAmount && (
                                            <div className="fieldMsg">{stepErrors.fmAmount}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="modalGrid4">
                                    <div className="field">
                                        <label>{optionalLabel("Monto efectivo")}</label>
                                        <input
                                            placeholder="0.00"
                                            value={stepData.cashAmount}
                                            onChange={(e) => {
                                                handlePriceInput(e);
                                                setStep({ cashAmount: e.target.value });
                                            }}
                                        />
                                    </div>

                                    <div className="field">
                                        <label>Pago online</label>
                                        <Select
                                            styles={reactSelectStyles}
                                            menuPortalTarget={document.body}
                                            options={ONLINE_PAY_OPTIONS}
                                            value={stepData.onlinePay}
                                            onChange={(v) => {
                                                if (String(v?.value) === "0")
                                                    setStep({ onlinePay: v, onlineAmount: "0.00" });
                                                else setStep({ onlinePay: v });
                                            }}
                                        />
                                    </div>

                                    <div
                                        className={`field ${stepErrors.onlineAmount ? "fieldError" : ""}`}
                                    >
                                        <label>Monto online</label>
                                        <input
                                            placeholder="0.00"
                                            value={stepData.onlineAmount}
                                            disabled={
                                                String(stepData.onlinePay?.value || "0") === "0"
                                            }
                                            onChange={(e) => {
                                                handlePriceInput(e);
                                                setStep({ onlineAmount: e.target.value });
                                            }}
                                        />
                                        {stepErrors.onlineAmount && (
                                            <div className="fieldMsg">{stepErrors.onlineAmount}</div>
                                        )}
                                    </div>

                                    <div
                                        className={`field ${stepErrors.contract_end_date ? "fieldError" : ""}`}
                                    >
                                        <label>{requiredLabel("Finalización de contrato")}</label>
                                        <input
                                            ref={contractEndDateRef}
                                            placeholder="Seleccionar fecha"
                                            value={stepData.contract_end_date || ""}
                                            readOnly
                                            onChange={(e) =>
                                                setStep({ contract_end_date: e.target.value })
                                            }
                                        />
                                        {stepErrors.contract_end_date && (
                                            <div className="fieldMsg">
                                                {stepErrors.contract_end_date}
                                            </div>
                                        )}
                                    </div>

                                    <div className="field">
                                        <label>
                                            <span className="labelRow">
                                                <span>Email</span>
                                                <span className="reqBadge" title="Obligatorio">
                                                    *
                                                </span>
                                            </span>
                                        </label>
                                        <input
                                            placeholder="correo@dominio.com"
                                            value={stepData.email || ""}
                                            onChange={(e) => {
                                                handleEmailInput(e);
                                                setStep({ email: e.target.value });
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className={`cardsReceiptSelector ${stepErrors.receiptType ? "hasError" : ""}`}>
                                    <div className="cardsReceiptHeader"><div><span>COMPROBANTE</span><h3>Documento para esta venta</h3></div><small>El RUC emisor se obtiene automáticamente del producto por IMEI.</small></div>
                                    <div className="cardsReceiptOptions">
                                        {RECEIPT_TYPE_OPTIONS.map((item) => (
                                            <button
                                                type="button"
                                                key={item.value}
                                                className={stepData.receiptType?.value === item.value ? "active" : ""}
                                                onClick={() => {
                                                    setStep({ receiptType: item });
                                                    if (item.value !== "FACTURA") resetRucLookupState();
                                                }}
                                            >
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                    {stepData.receiptType?.value === "FACTURA" && (
                                        <div className="modalGrid4 cardsInvoiceFields">
                                            <div className={`field ${stepErrors.customerRuc ? "fieldError" : ""}`}>
                                                <label>{requiredLabel("RUC del cliente")}</label>
                                                <div className="inputWithIcon">
                                                    <input
                                                        value={stepData.customerRuc}
                                                        maxLength={11}
                                                        inputMode="numeric"
                                                        autoComplete="off"
                                                        placeholder="11 dígitos"
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/\D/g, "").slice(0, 11);
                                                            rucLookupRequestRef.current += 1;
                                                            lastRucLookupRef.current = "";
                                                            setRucLookupLoading(false);
                                                            setRucLookupState({ status: "idle", message: "", ruc: value });
                                                            setStep({
                                                                customerRuc: value,
                                                                customerBusinessName: "",
                                                                customerAddress: "",
                                                            });
                                                            setStepErrors((prev) => ({
                                                                ...prev,
                                                                customerRuc: undefined,
                                                                customerBusinessName: undefined,
                                                                customerAddress: undefined,
                                                            }));
                                                            if (value.length === 11) {
                                                                window.setTimeout(() => lookupCustomerRuc(value), 0);
                                                            }
                                                        }}
                                                        onBlur={() => lookupCustomerRuc(stepData.customerRuc, { force: true })}
                                                    />
                                                    {rucLookupLoading ? (
                                                        <span className="miniSpinner" aria-label="Consultando RUC" />
                                                    ) : null}
                                                </div>
                                                {stepErrors.customerRuc && (
                                                    <div className="fieldMsg">{stepErrors.customerRuc}</div>
                                                )}
                                                {rucLookupState.message && (
                                                    <div className={`cardsRucLookupMessage is-${rucLookupState.status}`}>
                                                        {rucLookupState.message}
                                                    </div>
                                                )}
                                            </div>
                                            <div className={`field fieldSpan2 ${stepErrors.customerBusinessName ? "fieldError" : ""}`}>
                                                <label>{requiredLabel("Razón social")}</label>
                                                <input
                                                    value={stepData.customerBusinessName}
                                                    placeholder={rucLookupLoading ? "Consultando..." : "Se autocompleta con el RUC"}
                                                    onChange={(e) => setStep({ customerBusinessName: e.target.value })}
                                                />
                                                {stepErrors.customerBusinessName && (
                                                    <div className="fieldMsg">{stepErrors.customerBusinessName}</div>
                                                )}
                                            </div>
                                            <div className={`field ${stepErrors.customerAddress ? "fieldError" : ""}`}>
                                                <label>{requiredLabel("Dirección fiscal")}</label>
                                                <input
                                                    value={stepData.customerAddress}
                                                    placeholder={rucLookupLoading ? "Consultando..." : "Se autocompleta con el RUC"}
                                                    onChange={(e) => setStep({ customerAddress: e.target.value })}
                                                />
                                                {stepErrors.customerAddress && (
                                                    <div className="fieldMsg">{stepErrors.customerAddress}</div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {stepErrors.receiptType&&<div className="fieldMsg">{stepErrors.receiptType}</div>}
                                </div>

                                <CardsPromotionSelector open={stepModal.open} saleType={stepData.sale_type?.value || "credit"} eligible={Boolean(stepData.inventoryProductId && stepData.imei)} onChange={onPromoGiftsChange} apiBase={process.env.REACT_APP_API || ""} />

                                {requiresInitialValidation && (
                                    <div className={`cardsInitialValidationBox ${initialValidationCompleted ? "isCompleted" : ""}`}>
                                        <div className="cardsInitialValidationInfo">
                                            <span className="cardsInitialBadge">Validación de inicial</span>
                                            <h3>Valida la inicial del cliente antes de guardar</h3>
                                            <p>
                                                Esta validación graba al cliente confirmando su nombre y el monto de inicial pagado.
                                                Inicial detectada: <b>S/ {getInitialTotalAmount().toFixed(2)}</b>.
                                            </p>
                                            <div className="cardsInitialActions">
                                                <button
                                                    type="button"
                                                    className="cardsBtnPrimary"
                                                    onClick={createInitialValidationQr}
                                                    disabled={initialValidationLoading}
                                                >
                                                    {initialValidation.qrCode ? "Regenerar QR" : "Validar Inicial del Cliente"}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="cardsBtnGhost"
                                                    onClick={verifyInitialValidation}
                                                    disabled={initialValidationLoading}
                                                >
                                                    Verificar Validación
                                                </button>
                                            </div>
                                            {initialValidation.message && (
                                                <div className="modalHint">{initialValidation.message}</div>
                                            )}
                                            {stepErrors.initial_validation && (
                                                <div className="fieldMsg">{stepErrors.initial_validation}</div>
                                            )}
                                        </div>

                                        {initialValidation.qrCode && (
                                            <div className="cardsInitialQrCard">
                                                <img src={initialValidation.qrCode} alt="QR de validación de inicial" />
                                                <strong>
                                                    {initialValidationCompleted ? "Validación completada" : "Pendiente de grabación"}
                                                </strong>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="modalFooter">
                        <button className="cardsBtnGhost" onClick={closeStepModal}>
                            Cancelar
                        </button>
                        {requiresInitialValidation && !initialValidationCompleted ? (
                            <button
                                className="cardsBtnPrimary"
                                type="button"
                                onClick={createInitialValidationQr}
                                disabled={initialValidationLoading}
                            >
                                Validar Inicial del Cliente
                            </button>
                        ) : (
                            <button className="cardsBtnPrimary" onClick={onSaveStep2WithReview} disabled={savingStep2 || loading}>
                                {savingStep2 ? "Guardando..." : "Guardar registro"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderSaleReviewPopup = () => {
        if (!saleReviewPopup.open) return null;

        if (saleReviewPopup.context === "prospect") {
            const payload = pendingCreatePayload || {};
            const fullName = `${payload.name || ""} ${payload.paternalSur || ""} ${payload.maternalSur || ""}`
                .replace(/\s+/g, " ")
                .trim();

            return (
                <SaleReviewPopup
                    open
                    step={1}
                    totalSteps={1}
                    eyebrow="Validación del prospecto"
                    title="Confirma la identidad del cliente"
                    confirmLabel="Confirmar y guardar prospecto"
                    onConfirm={confirmSaleReviewPopup}
                    onCancel={closeSaleReviewPopup}
                >
                    <p>Antes de registrar el prospecto, compara el DNI y el nombre completo con el documento del cliente.</p>
                    <div className="cardsSaleReviewSummary">
                        <div><span>DNI</span><strong>{payload.dni || "—"}</strong></div>
                        <div><span>Nombre completo</span><strong>{fullName || "—"}</strong></div>
                    </div>
                    <p className="cardsSaleReviewWarning">No continúes si el número de DNI o cualquiera de los nombres y apellidos no corresponde exactamente al cliente.</p>
                </SaleReviewPopup>
            );
        }

        if (saleReviewPopup.context !== "sale" || Number(stepModal.state) !== 3) return null;

        const reviewStep = Number(saleReviewPopup.step || 1);
        const stepKeys = getSaleReviewStepKeys();
        const reviewKey = stepKeys[reviewStep - 1] || stepKeys[0];
        const totalSteps = stepKeys.length;
        const saleTypeLabel = stepData.sale_type?.label || "—";
        const financeLabel = getSaleReviewFinanceLabel();
        const cashAmount = moneyToNumber(stepData.cashAmount);
        const onlineAmount =
            String(stepData.onlinePay?.value || "0") === "0"
                ? 0
                : moneyToNumber(stepData.onlineAmount);
        const fmAmount =
            stepData.sale_type?.value === "credit" && String(stepData.fm?.value || "0") === "1"
                ? moneyToNumber(stepData.fmAmount)
                : 0;
        const paymentTotal = cashAmount + onlineAmount;
        const initialTotal = paymentTotal + fmAmount;
        const equipmentLabel = stepData.phoneValue?.raw?.title || stepData.phone || "—";
        const receiptLabel = stepData.receiptType?.label || "—";
        const isLastStep = reviewStep >= totalSteps;

        const commonProps = {
            open: true,
            step: reviewStep,
            totalSteps,
            confirmLabel: isLastStep ? "Confirmar y guardar venta" : "Confirmar y continuar",
            onConfirm: confirmSaleReviewPopup,
            onCancel: closeSaleReviewPopup,
        };

        if (reviewKey === "sale_type") {
            return (
                <SaleReviewPopup
                    {...commonProps}
                    eyebrow={`Revisión ${reviewStep} de ${totalSteps} · Tipo de venta`}
                    title="¿La venta es realmente Contado o Crédito?"
                >
                    <p>Verifica el tipo de operación antes de continuar. Esta selección determina la financiera, FM y el tratamiento de los montos.</p>
                    <div className="cardsSaleReviewValue">
                        <span>Tipo seleccionado</span>
                        <strong>{saleTypeLabel}</strong>
                    </div>
                    <p className="cardsSaleReviewWarning">Confirma que el tipo seleccionado coincide con la operación real realizada con el cliente.</p>
                </SaleReviewPopup>
            );
        }

        if (reviewKey === "finance") {
            return (
                <SaleReviewPopup
                    {...commonProps}
                    eyebrow={`Revisión ${reviewStep} de ${totalSteps} · Financiera`}
                    title="Confirma la fuente / financiera"
                >
                    <p>En ventas a crédito es indispensable confirmar la financiera correcta antes de registrar la operación.</p>
                    <div className="cardsSaleReviewValue">
                        <span>Fuente seleccionada</span>
                        <strong>{financeLabel}</strong>
                    </div>
                    <p className="cardsSaleReviewWarning">Revisa especialmente este dato: una financiera incorrecta afecta el registro comercial y los reportes posteriores.</p>
                </SaleReviewPopup>
            );
        }

        if (reviewKey === "equipment") {
            return (
                <SaleReviewPopup
                    {...commonProps}
                    eyebrow={`Revisión ${reviewStep} de ${totalSteps} · Equipo`}
                    title="Confirma el equipo exacto que se va a vender"
                >
                    <p>Compara físicamente el equipo con el producto seleccionado en el sistema.</p>
                    <div className="cardsSaleReviewSummary">
                        <div><span>Equipo / Modelo</span><strong>{equipmentLabel}</strong></div>
                        <div><span>IMEI</span><strong>{stepData.imei || "—"}</strong></div>
                    </div>
                    <p className="cardsSaleReviewWarning"><b>Revisa cuidadosamente RAM, ROM/capacidad, color y variante del modelo.</b> No confirmes si alguna propiedad no coincide con el equipo físico.</p>
                </SaleReviewPopup>
            );
        }

        if (reviewKey === "fm") {
            const hasFm = String(stepData.fm?.value || "0") === "1";
            return (
                <SaleReviewPopup
                    {...commonProps}
                    eyebrow={`Revisión ${reviewStep} de ${totalSteps} · FM`}
                    title="¿Esta venta cuenta con FM?"
                >
                    <p>Confirma si la operación tiene un FM asociado antes de continuar.</p>
                    <div className="cardsSaleReviewValue">
                        <span>FM seleccionado</span>
                        <strong>{hasFm ? "Sí" : "No"}</strong>
                    </div>
                    <p className="cardsSaleReviewWarning">Si el cliente tiene FM, selecciona Sí y asegúrate de que el monto esté registrado correctamente.</p>
                </SaleReviewPopup>
            );
        }

        if (reviewKey === "fm_amount") {
            return (
                <SaleReviewPopup
                    {...commonProps}
                    eyebrow={`Revisión ${reviewStep} de ${totalSteps} · Monto FM`}
                    title="Confirma el monto FM"
                >
                    <p>La venta está marcada con FM. Verifica que el importe registrado sea exactamente el autorizado para esta operación.</p>
                    <div className="cardsSaleReviewValue">
                        <span>Monto FM</span>
                        <strong>S/ {fmAmount.toFixed(2)}</strong>
                    </div>
                    <p className="cardsSaleReviewWarning">No continúes si el monto FM difiere del monto realmente aplicado al cliente.</p>
                </SaleReviewPopup>
            );
        }

        if (reviewKey === "payment") {
            return (
                <SaleReviewPopup
                    {...commonProps}
                    eyebrow={`Revisión ${reviewStep} de ${totalSteps} · Precio y pago`}
                    title="Confirma el precio y cómo pagó el cliente"
                >
                    <p>Revisa el precio del equipo y la distribución del pago. El cliente puede pagar todo en efectivo, todo online o combinar ambos métodos.</p>
                    <div className="cardsSaleReviewSummary">
                        <div><span>Precio del equipo</span><strong>S/ {moneyToNumber(stepData.phonePrice).toFixed(2)}</strong></div>
                        <div><span>Efectivo</span><strong>S/ {cashAmount.toFixed(2)}</strong></div>
                        <div><span>Método online</span><strong>{stepData.onlinePay?.label || "No"}</strong></div>
                        <div><span>Monto online</span><strong>S/ {onlineAmount.toFixed(2)}</strong></div>
                        <div><span>Total efectivo + online</span><strong>S/ {paymentTotal.toFixed(2)}</strong></div>
                        {stepData.sale_type?.value === "credit" && (
                            <div><span>Inicial total incluyendo FM</span><strong>S/ {initialTotal.toFixed(2)}</strong></div>
                        )}
                    </div>
                    <p className="cardsSaleReviewHint">Métodos online disponibles: Yape, Culqi Yape, Tarjeta, Culqi Tarjeta y Transferencia.</p>
                    <p className="cardsSaleReviewWarning">Confirma que cada monto coincide con el dinero realmente recibido. Si hubo pago mixto, revisa por separado efectivo y online.</p>
                </SaleReviewPopup>
            );
        }

        return (
            <SaleReviewPopup
                {...commonProps}
                eyebrow={`Revisión ${reviewStep} de ${totalSteps} · Comprobante`}
                title="Confirma el comprobante que se va a generar"
            >
                <p>La opción predeterminada puede ser Boleta, pero debes confirmar el documento solicitado para esta venta antes de emitirlo.</p>
                <div className="cardsSaleReviewValue">
                    <span>Comprobante seleccionado</span>
                    <strong>{receiptLabel}</strong>
                </div>
                {stepData.sale_type?.value === "credit" && String(stepData.finance?.value || "") === "3" && (
                    <div className="cardsSaleReviewSummary">
                        <div><span>Modalidad</span><strong>Efectiva · 2 comprobantes</strong></div>
                        <div><span>Anticipo / inicial</span><strong>S/ {paymentTotal.toFixed(2)}</strong></div>
                        <div><span>Precio del equipo</span><strong>S/ {moneyToNumber(stepData.phonePrice).toFixed(2)}</strong></div>
                        <div><span>Saldo</span><strong>S/ {Math.max(0, moneyToNumber(stepData.phonePrice) - paymentTotal).toFixed(2)}</strong></div>
                    </div>
                )}
                {stepData.sale_type?.value === "credit" && String(stepData.finance?.value || "") === "3" && (
                    <p className="cardsSaleReviewHint">Efectiva generará un comprobante por la inicial del cliente y un segundo comprobante del equipo aplicando ese anticipo. La venta y el IMEI se registran una sola vez.</p>
                )}
                {stepData.receiptType?.value === "FACTURA" && (
                    <div className="cardsSaleReviewSummary">
                        <div><span>RUC del cliente</span><strong>{stepData.customerRuc || "—"}</strong></div>
                        <div><span>Razón social</span><strong>{stepData.customerBusinessName || "—"}</strong></div>
                    </div>
                )}
                <p className="cardsSaleReviewWarning">Esta es la última revisión. Si el comprobante no es correcto, vuelve al formulario y cámbialo antes de guardar.</p>
            </SaleReviewPopup>
        );
    };

    const renderManagerModal = () => {
        if (!managerModal.open) return null;
        return (
            <div className="modalOverlay" onMouseDown={closeManagerModal}>
                <div
                    className="modalCard popAnim"
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <div className="modalTop">
                        <div className="modalTitle">Asignar gestor</div>
                        <button className="cardsBtnGhost" onClick={closeManagerModal}>
                            Cerrar
                        </button>
                    </div>

                    <div className="modalBody">
                        <div className="field">
                            <label>{requiredLabel("Gestor de créditos y cobranzas")}</label>
                            <Select
                                styles={reactSelectStyles}
                                menuPortalTarget={document.body}
                                options={managersOptions}
                                value={selectedManager}
                                onChange={(v) => setSelectedManager(v)}
                                placeholder="Seleccionar gestor..."
                            />
                        </div>
                    </div>

                    <div className="modalFooter">
                        <button className="cardsBtnGhost" onClick={closeManagerModal}>
                            Cancelar
                        </button>
                        <button className="cardsBtnPrimary" onClick={onSaveManager}>
                            Guardar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            {loading && <PhoneLoader />}
            <ScrollToTop />
            <Header user={user} namePage="Cards" />
            <style>{`
        .cardsInitialValidationBox{margin-top:16px;border:1px solid rgba(77,0,132,.18);background:linear-gradient(135deg,rgba(77,0,132,.06),rgba(255,255,255,.96));border-radius:18px;padding:16px;display:grid;grid-template-columns:1fr 190px;gap:16px;align-items:center;}
        .cardsInitialValidationBox.isCompleted{border-color:rgba(16,185,129,.35);background:linear-gradient(135deg,rgba(16,185,129,.08),#fff);}
        .cardsInitialBadge{display:inline-flex;background:#4d0084;color:#fff;border-radius:999px;padding:5px 10px;font-size:12px;font-weight:800;margin-bottom:8px;}
        .cardsInitialValidationInfo h3{margin:0 0 6px;font-size:16px;color:#111827;}
        .cardsInitialValidationInfo p{margin:0 0 12px;color:#475569;font-size:13px;line-height:1.45;}
        .cardsInitialActions{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:10px;}
        .cardsInitialQrCard{background:#fff;border:1px solid #e5e7eb;border-radius:16px;padding:12px;text-align:center;box-shadow:0 10px 24px rgba(15,23,42,.08);}
        .cardsInitialQrCard img{width:150px;height:150px;object-fit:contain;display:block;margin:0 auto 8px;}
        .cardsInitialQrCard strong{display:block;color:#111827;font-size:13px;}
        .cardsInventorySedeHint{display:block;margin-top:6px;color:#64748b;font-size:11px;line-height:1.35;}
        @media(max-width:768px){.cardsInitialValidationBox{grid-template-columns:1fr}.cardsInitialQrCard img{width:190px;height:190px}}
      `}</style>

            <section className="sectionBody sectionBodyCards sectionBodyInstallment">
                <div className="containerCustom containerBody">
                    <div className="blockBody installmentBlock">
                        <div
                            className={`cardsHeader ${isRole3 ? "cardsHeaderRole3" : "cardsHeaderAdmin"}`}
                        >
                            <div className="cardsHeaderLeft">
                                {!isRole3 ? (
                                    <>
                                        <label className="cardsLabel">Asesor</label>
                                        <div className="cardsSelectWrap">
                                            <Select
                                                options={advisorFilterOptions}
                                                value={selectedAdvisorOption}
                                                styles={headerFilterSelectStyles}
                                                menuPortalTarget={document.body}
                                                onChange={(option) =>
                                                    onAdvisorChange(option?.value ?? "0")
                                                }
                                                placeholder="Seleccionar asesor"
                                                classNamePrefix="cardsFilterSelect"
                                                isSearchable
                                            />
                                        </div>

                                        <label className="cardsLabel">Sede</label>
                                        <div className="sedeStyleOne">
                                            <Select
                                                options={sedesFilter}
                                                value={selectedSedeOption}
                                                styles={headerFilterSelectStyles}
                                                menuPortalTarget={document.body}
                                                onChange={onSedeChange}
                                                placeholder="Seleccionar sede"
                                                classNamePrefix="sedesSelect"
                                                formatGroupLabel={(group) => (
                                                    <div className="sedesSelectGroupHeading">
                                                        {group.label} ({Number(group.total || 0)})
                                                    </div>
                                                )}
                                            />
                                        </div>

                                        <label className="cardsLabel">Gestor</label>
                                        <div className="cardsSelectWrap">
                                            <Select
                                                options={managerFilterOptions}
                                                value={selectedManagerOption}
                                                styles={headerFilterSelectStyles}
                                                menuPortalTarget={document.body}
                                                onChange={(option) =>
                                                    onManagerChange(option?.value ?? "0")
                                                }
                                                placeholder="Seleccionar gestor"
                                                classNamePrefix="cardsFilterSelect"
                                                isSearchable
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <button className="cardsRole3Pill" type="button" disabled>
                                        Mostrando solo tus prospectos
                                    </button>
                                )}

                                {isRole3 && (
                                    <button className="cardsBtnPrimary" onClick={openCreateCard}>
                                        Registrar Prospecto
                                    </button>
                                )}
                            </div>

                            <div className="cardsHeaderRight">
                                <div className="cardsSearchWrap cardsCalendarWrap">
                                    <div className="cardsRole3DateLabel">Buscador:</div>
                                    <input
                                        className="cardsSearch"
                                        placeholder="Buscar por DNI, nombre o celular..."
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                const t = String(q || "").trim();
                                                if (t.length >= 3 || t.length === 0)
                                                    fetchCards({ q: t });
                                            }
                                        }}
                                    />
                                </div>

                                {!isRole3 ? (
                                    <div className="cardsCalendarWrap">
                                        <div className="cardsCalendarText">{showText}</div>
                                        <input
                                            ref={calendarInputRef}
                                            className="cardsCalendarInput"
                                            placeholder="Seleccionar rango"
                                        />
                                    </div>
                                ) : (
                                    <div className="cardsRole3DateBox">
                                        <div className="cardsRole3DateLabel">Fecha visible</div>
                                        <div className="cardsRole3DateValue">{showText}</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="cardsTabs">
                            {tabs.map((t) => (
                                <button
                                    key={t.key}
                                    className={`cardsTab ${String(saleStateTab) === String(t.key) ? "active" : ""}`}
                                    onClick={() => onChangeTab(t.key)}
                                >
                                    <span className="tabLabel">{t.label}</span>
                                    <span className="tabCount">
                                        {tabCounts[String(t.key)] ?? 0}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {showCreate && renderCreate()}

                        <div className="cardsGrid">
                            {cards.map((c) => renderCard(c))}
                            {!loading && cards.length === 0 && (
                                <div className="emptyBox">No hay prospectos para mostrar.</div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {renderStepModal()}
            {renderSaleReviewPopup()}
            {renderManagerModal()}

            {saleCompletion && (
                <div className="saleCompletionOverlay" role="dialog" aria-modal="true">
                    <div className="saleCompletionModal">
                        <button type="button" className="saleCompletionClose" onClick={() => setSaleCompletion(null)}>×</button>
                        <div className={`saleCompletionIcon ${saleCompletion.sunatPending ? "warning" : "success"}`}>
                            {saleCompletion.sunatPending ? "!" : "✓"}
                        </div>
                        <h2>Venta realizada correctamente</h2>
                        <p className="saleCompletionLead">
                            El equipo quedó registrado como vendido y el IMEI ya no está disponible para otra venta.
                        </p>
                        <div className="saleCompletionSummary">
                            {saleCompletion.efectivaDualReceipt && saleCompletion.advanceReceipt && saleCompletion.finalReceipt ? (
                                <>
                                    <div><span>Anticipo</span><strong>{saleCompletion.advanceReceipt.fullNumber || "—"} · S/ {Number(saleCompletion.advanceReceipt.amount || 0).toFixed(2)}</strong></div>
                                    <div><span>Comprobante final</span><strong>{saleCompletion.finalReceipt.fullNumber || "—"} · S/ {Number(saleCompletion.finalReceipt.amount || 0).toFixed(2)}</strong></div>
                                </>
                            ) : (
                                <div><span>Comprobante</span><strong>{saleCompletion.fullNumber || "—"}</strong></div>
                            )}
                            <div><span>Estado SUNAT</span><strong>{saleCompletion.sunatPending ? "Pendiente de envío" : "Procesado"}</strong></div>
                        </div>
                        {saleCompletion.sunatPending && (
                            <div className="saleCompletionNotice">
                                SUNAT no respondió en este momento. La venta se guardó correctamente y el mismo comprobante podrá reenviarse desde Inventario → Comprobantes.
                            </div>
                        )}
                        <div className="saleCompletionActions">
                            <button type="button" className="secondary" onClick={() => setSaleCompletion(null)}>Cerrar</button>
                            {String(saleCompletion.documentType || "") === "03" && saleCompletion.efectivaDualReceipt && saleCompletion.advanceReceipt && saleCompletion.finalReceipt ? (
                                <>
                                    <button type="button" className="primary" onClick={() => downloadSaleCompletionDocument(saleCompletion, saleCompletion.advanceReceipt).catch((e) => alert(e?.response?.data?.error || "No se pudo descargar el anticipo."))}>Descargar Anticipo</button>
                                    <button type="button" className="primary" onClick={() => downloadSaleCompletionDocument(saleCompletion, saleCompletion.finalReceipt).catch((e) => alert(e?.response?.data?.error || "No se pudo descargar la boleta final."))}>Descargar Boleta Final</button>
                                </>
                            ) : String(saleCompletion.documentType || "") === "03" && (
                                <button type="button" className="primary" onClick={() => downloadSaleCompletionDocument(saleCompletion).catch((e) => alert(e?.response?.data?.error || "No se pudo descargar la boleta."))}>
                                    Descargar Boleta
                                </button>
                            )}
                            {String(saleCompletion.documentType || "") === "01" && (
                                <button type="button" className="primary" onClick={() => downloadSaleCompletionDocument(saleCompletion).catch((e) => alert(e?.response?.data?.error || "No se pudo descargar el PDF A4."))}>
                                    Descargar PDF A4
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <FmFinalizePopup
                open={showFmModal}
                onClose={() => {
                    setShowFmModal(false);
                    setPendingFmContext(null);
                    document.body.classList.remove("no-scroll");
                }}
                initial={{
                    category: pendingFmContext?.popupInitial?.category,
                    cat: pendingFmContext?.popupInitial?.cat,
                    subCategory: pendingFmContext?.popupInitial?.subCategory,
                    subCat: pendingFmContext?.popupInitial?.subCat,
                    segment: pendingFmContext?.popupInitial?.segment,
                    amountFM: pendingFmContext?.popupInitial?.amountFM,
                    dni: pendingFmContext?.popupInitial?.dni,
                    name: pendingFmContext?.popupInitial?.name,
                    paternalSur: pendingFmContext?.popupInitial?.paternalSur,
                    maternalSur: pendingFmContext?.popupInitial?.maternalSur,
                    email: pendingFmContext?.popupInitial?.email,
                    addressName: pendingFmContext?.popupInitial?.addressName || "",
                    phoneNumber: pendingFmContext?.popupInitial?.phoneNumber,
                    modelPhone: pendingFmContext?.popupInitial?.modelPhone,
                    imei: pendingFmContext?.popupInitial?.imei,
                }}
                forcePayjoyMode={Boolean(pendingFmContext?.forcePayjoyMode)}
                hideCuota={Boolean(pendingFmContext?.hideCuota)}
                onCreated={async () => {
                    if (!pendingFmContext?.cardId || !pendingFmContext?.soldPayload)
                        return;
                    setLoading(true);
                    try {
                        await persistCardUpdate(
                            pendingFmContext.cardId,
                            pendingFmContext.soldPayload,
                            getSuccessToastMessage(3, true),
                        );
                        setShowFmModal(false);
                        setPendingFmContext(null);
                        document.body.classList.remove("no-scroll");
                    } catch (e) {
                        console.error(e);
                        alert(e?.response?.data?.error || "Error guardando venta con FM");
                    } finally {
                        setLoading(false);
                    }
                }}
            />
        </div>
    );
}
